import { useEffect, useState, useMemo, useRef, Fragment } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadXlsx } from "@/lib/download";
import { useSortable, SortableTh } from "@/lib/sort";
import { buildMoleculeGroups } from "@/lib/molecule-groups";
import { StudyCnSearch } from "@/components/dispensation/StudyCnSearch";
import { useCurrentStudy } from "@/context/CurrentStudyContext";
import { toPng } from "html-to-image";
import {
  ArrowLeft,
  Building2,
  ArrowRightLeft,
  Save,
  Trophy,
  Download,
  FileText,
  ChevronDown,
  ChevronRight,
  DollarSign,
  TrendingUp,
  Share2,
  AlertTriangle,
  X,
  Users,
  Sparkles,
  Check,
  Presentation,
  Printer,
  Filter,
  Star,
  Gauge,
  Layers,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import { useTableSort, SortTh } from "@/lib/sortable";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";
import Top25BestOptionModal from "@/components/top25/Top25BestOptionModal";


const COLOR = {
  green: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500", label: "Alta" },
  yellow: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500", label: "Media" },
  red: { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500", label: "Baja" },
};

// Descripción del rango de cuota de genérico de cada estado (% sobre uds. EFG)
const STATE_RANGE = {
  perfecto:     "100% genérico",
  excelente:    "75 – 99,9%",
  mas_generico: "50 – 74,9%",
  mas_marca:    "30 – 49,9%",
  mejorar:      "15 – 29,9%",
  poco_cambio:  "5 – 14,9%",
  marginal:     ">0 – 4,9%",
  solo_marca:   "0% genérico",
};
const STATE_HELP = {
  perfecto:     "Sólo dispensas genérico. Objetivo cumplido al 100% en esta molécula.",
  excelente:    "Cuota de genérico entre 75% y 99,9%. Casi perfecto: queda muy poco para alcanzar el 100%.",
  mas_generico: "Cuota de genérico entre 50% y 74,9%. Mayoritariamente genérico, pero aún despachas marca con frecuencia.",
  mas_marca:    "Cuota de genérico entre 30% y 49,9%. Despachas más marca que genérico — claro margen de mejora.",
  mejorar:      "Cuota de genérico entre 15% y 29,9%. Predominio claro de marca; objetivo prioritario para el cambio.",
  poco_cambio:  "Cuota de genérico entre 5% y 14,9%. Sustitución muy esporádica.",
  marginal:     "Cuota de genérico superior a 0% y por debajo del 5%. Casi nunca sustituyes la marca.",
  solo_marca:   "0% de genérico. Esta molécula la despachas exclusivamente en marca, aunque hay alternativa EFG disponible.",
};

// Estado por % cambio (cuota de genérico) en la molécula
const STATE = {
  perfecto:     { label: "PERFECTO",                bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500", border: "border-emerald-500" },
  excelente:    { label: "EXCELENTE",               bg: "bg-emerald-50",  text: "text-emerald-700", dot: "bg-emerald-400", border: "border-emerald-400" },
  mas_generico: { label: "Más genérico que marca",  bg: "bg-lime-50",     text: "text-lime-800",    dot: "bg-lime-500",    border: "border-lime-500" },
  mas_marca:    { label: "Más marca que genérico",  bg: "bg-amber-50",    text: "text-amber-800",   dot: "bg-amber-500",   border: "border-amber-500" },
  mejorar:      { label: "MEJORAR",                 bg: "bg-orange-50",   text: "text-orange-800",  dot: "bg-orange-500",  border: "border-orange-500" },
  poco_cambio:  { label: "Poco cambio",             bg: "bg-red-50",      text: "text-red-700",     dot: "bg-red-500",     border: "border-red-500" },
  marginal:     { label: "Marginal",                bg: "bg-rose-100",    text: "text-rose-900",    dot: "bg-rose-700",    border: "border-rose-700" },
  solo_marca:   { label: "SOLO MARCA",              bg: "bg-slate-900",   text: "text-white",       dot: "bg-slate-900",   border: "border-slate-900" },
  no_data:      { label: "Sin EFG",                 bg: "bg-slate-50",    text: "text-slate-500",   dot: "bg-slate-400",   border: "border-slate-300" },
};

const StateBadge = ({ stateKey, pct }) => {
  const st = STATE[stateKey] || STATE.no_data;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
      {st.label}
      {pct != null && <span className="font-mono-num opacity-75">· {pct}%</span>}
    </span>
  );
};

const fmtEur = (n) =>
  Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const fmtNum = (n) => Number(n || 0).toLocaleString("es-ES");

export default function DispensationDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const initialTab = (() => {
    const t = (searchParams.get("tab") || "").toLowerCase();
    const allowed = ["overview", "molecules", "variability", "top", "marca", "suppliers"];
    // Iter-137 · por defecto se abre "Moléculas" (no el semáforo)
    return allowed.includes(t) ? t : "molecules";
  })();
  const [tab, setTab] = useState(initialTab);
  const [colorList, setColorList] = useState(null); // tramo del semáforo u/mes abierto


  // Si la URL trae ?sort=... &dir=... aplicamos esa preferencia al storage del
  // hook useSortable correspondiente a la tab activa, para que la tabla se cargue
  // ordenada como pidió el dashboard.
  useEffect(() => {
    const sort = searchParams.get("sort");
    const dir = (searchParams.get("dir") || "desc").toLowerCase() === "asc" ? "asc" : "desc";
    if (!sort) return;
    const storageKey = initialTab === "marca" ? "disp-marca"
      : initialTab === "molecules" ? "disp-molecules-v2"
      : initialTab === "overview" ? "disp-generic-v2"
      : null;
    if (!storageKey) return;
    try {
      localStorage.setItem(`sort:${storageKey}`, JSON.stringify({ key: sort, dir }));
    } catch {/* ignore */}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si entramos con ?tab=... (p. ej. desde "Moléculas que no tenemos") bajamos
  // directamente a la tabla de esa pestaña en vez de dejar la vista arriba.
  useEffect(() => {
    if (!data || !searchParams.get("tab")) return;
    const el = document.querySelector("[data-testid=detail-tabs]");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const [labs, setLabs] = useState([]);
  const [labsCatalog, setLabsCatalog] = useState([]); // full catalog for redistribute
  const [molStateFilter, setMolStateFilter] = useState(
    (searchParams.get("state") || "").toLowerCase()
  );
  const [molLabFilter, setMolLabFilter] = useState("");      // filtra por preferred_lab
  const [molAutoOnly, setMolAutoOnly] = useState(false);     // sólo asignaciones automáticas

  const focusMoleculesByLab = (lab, autoOnly = true) => {
    setMolLabFilter(lab);
    setMolAutoOnly(autoOnly);
    setMolStateFilter("");
    setTab("molecules");
  };

  const reload = () => {
    api.get(`/dispensations/${id}`).then((r) => {
      setData(r.data);
      const auto = r.data?.auto_assigned_preferences || [];
      const re = r.data?.auto_reassigned_preferences || [];
      if (auto.length > 0) {
        toast.success(
          `Auto-asignado proveedor a ${auto.length} ${auto.length === 1 ? "molécula" : "moléculas"} sin asignar (mayor stock entre tus proveedores con condición)`,
          { duration: 6000 },
        );
      }
      if (re.length > 0) {
        const sample = re.slice(0, 3).map((x) => `${x.previous_lab} → ${x.preferred_lab}`).join(" · ");
        const extra = re.length > 3 ? ` (+${re.length - 3} más)` : "";
        toast.info(
          `Re-asignadas ${re.length} ${re.length === 1 ? "molécula" : "moléculas"} por cambio de stock: ${sample}${extra}`,
          { duration: 8000 },
        );
      }
    });
  };
  useEffect(reload, [id]);

  // Marcar este estudio como "vigente" al entrar
  const { setCurrent, current } = useCurrentStudy();
  useEffect(() => {
    if (id && current?.id !== id) {
      setCurrent(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    api.get("/catalog").then((r) => {
      setLabsCatalog(r.data);
      const set = new Set();
      r.data.forEach((c) => c.laboratorio && set.add(c.laboratorio));
      setLabs(Array.from(set).sort());
    });
  }, []);

  // Iter-63: labs de marca con descuento negociado extra → estrellita en las
  // líneas de marca (tabla y expansión). Fetch único; refresca al cambiar de
  // estudio o cuando se guarda desde BrandDiscountsModal (evento custom).
  const [brandDiscountLabs, setBrandDiscountLabs] = useState(() => new Set());
  const loadBrandDiscountLabs = () => {
    api.get("/supplier-agreements").then((r) => {
      const s = new Set();
      (r.data?.items || []).forEach((a) => {
        if ((Number(a.brand_discount_pct) || 0) > 0 && a.laboratorio) {
          s.add(a.laboratorio.trim().toUpperCase());
        }
      });
      setBrandDiscountLabs(s);
    }).catch(() => { /* silent */ });
  };
  useEffect(() => {
    loadBrandDiscountLabs();
    const onSaved = () => loadBrandDiscountLabs();
    window.addEventListener("brand-discounts-saved", onSaved);
    return () => window.removeEventListener("brand-discounts-saved", onSaved);
  }, []);

  const molMap = useMemo(() => {
    if (!data) return {};
    return Object.fromEntries(data.molecules.map((m) => [m.gh, m]));
  }, [data]);

  if (!data) return <div className="p-12 text-slate-400">Cargando…</div>;

  const s = data.summary;
  const efgTotalUnits = s.marca_units + s.generico_units;
  const efgTotalEur = (s.marca_euros || 0) + (s.generico_euros || 0);
  const marcaPct = efgTotalUnits ? Math.round((s.marca_units / efgTotalUnits) * 100) : 0;
  const genericoPct = efgTotalUnits ? 100 - marcaPct : 0;
  const marcaEurPct = efgTotalEur ? Math.round((s.marca_euros / efgTotalEur) * 100) : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1500px]" data-testid="dispensation-detail-page">
      <Link
        to="/history"
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6"
        data-testid="back-link"
      >
        <ArrowLeft className="h-4 w-4" /> Volver al historial
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6 sm:mb-8">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
            Análisis · {data.period_start} → {data.period_end} · {data.months}{" "}
            {data.months === 1 ? "mes" : "meses"}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tighter text-slate-950 break-words">
            {data.label || data.filename}
          </h1>
          {data.stock_meta?.has_stock && (
            <div className="mt-3 inline-flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1.5 rounded-full" data-testid="stock-badge">
              <span className="text-emerald-600">●</span>
              <strong>Stock a {new Date(data.stock_meta.uploaded_at).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })}</strong>
              <span className="text-emerald-700">· {data.stock_meta.total_cn} CN cargados</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="export-pdf-button"
            onClick={() => downloadXlsx(`/dispensations/${id}/report`, "informe.pdf")}
            className="inline-flex items-center gap-2 bg-slate-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800"
          >
            <FileText className="h-4 w-4" strokeWidth={1.5} />
            Informe PDF
          </button>
          <button
            data-testid="export-detail-button"
            onClick={() => downloadXlsx(`/dispensations/${id}/export`, "farmaclass.xlsx")}
            className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-900 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-50"
          >
            <Download className="h-4 w-4" strokeWidth={1.5} />
            Exportar Excel
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {(() => {
          const efgTotal = (s.marca_euros || 0) + (s.generico_euros || 0);
          const noEfg = Math.max(0, (data.total_euros || 0) - efgTotal);
          return (
            <Kpi
              label="Total € EFG"
              value={fmtEur(efgTotal)}
              accent="bg-slate-950 text-white"
              sub={noEfg > 0.5 ? `Marca + Genérico EFG · ${fmtEur(noEfg)} no EFG excluidos` : "Marca + Genérico EFG"}
              hint={
                noEfg > 0.5
                  ? `Esta cifra es la SUMA exacta de € Marca + € Genérico (todo dentro del universo EFG). Se han excluido del total ${fmtEur(noEfg)} en productos no susceptibles de conversión a genérico (parafarmacia, productos sin G.H., no sustituibles…).`
                  : "Esta cifra es la suma de € Marca + € Genérico (universo EFG). Todos los productos del estudio son EFG."
              }
            />
          );
        })()}
        <Kpi
          label="Unidades EFG"
          value={fmtNum((s.marca_units || 0) + (s.generico_units || 0))}
          sub={data.total_units && ((s.marca_units || 0) + (s.generico_units || 0)) < data.total_units
            ? `${fmtNum(data.total_units - ((s.marca_units || 0) + (s.generico_units || 0)))} uds no EFG excluidas`
            : "Marca + Genérico EFG"}
          hint="Suma de unidades dentro del universo EFG (marca sustituible + genérico). Excluye productos sin G.H. o no sustituibles."
        />
        <Kpi
          label="€ marca"
          value={fmtEur(s.marca_euros)}
          sub={`${marcaEurPct}% del EFG`}
          hint="Importe dispensado en productos de marca dentro del universo EFG (con G.H. sustituible). Es la parte donde puedes convertir a genérico."
        />
        <Kpi
          label="€ genérico"
          value={fmtEur(s.generico_euros)}
          sub={`${100 - marcaEurPct}% del EFG`}
          hint="Importe dispensado en productos genéricos (EFG) dentro del universo EFG. Cuanto más alto, mejor."
        />
      </div>

      {/* Marca vs Generico */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-1">
          Reparto EFG
        </div>
        <h2 className="font-display text-2xl font-semibold tracking-tight mb-4">Marca vs Genérico</h2>
        {efgTotalUnits > 0 ? (
          <div className="space-y-3">
            <BarRow
              label="Unidades"
              marca={s.marca_units}
              generico={s.generico_units}
              marcaPct={marcaPct}
              genericoPct={genericoPct}
              fmt={fmtNum}
            />
            <BarRow
              label="Euros"
              marca={s.marca_euros}
              generico={s.generico_euros}
              marcaPct={marcaEurPct}
              genericoPct={100 - marcaEurPct}
              fmt={fmtEur}
            />
          </div>
        ) : (
          <div className="text-sm text-slate-500">Ningún producto EFG identificado.</div>
        )}
      </div>

      {/* State breakdown (% cambio por molécula) */}
      <div className="mb-8" data-testid="state-breakdown">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">
          Estado por % de cambio · molécula (G.H.) — clic para listarlas
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {["perfecto", "excelente", "mas_generico", "mas_marca", "mejorar", "poco_cambio", "marginal", "solo_marca"].map((k) => {
            const item = (data.state_breakdown || []).find((x) => x.key === k) || { molecules: 0, marca_euros: 0, generico_euros: 0 };
            const st = STATE[k];
            return (
              <button
                key={k}
                data-testid={`state-card-${k}`}
                onClick={() => {
                  setMolStateFilter(k);
                  setTab("molecules");
                }}
                title={STATE_HELP[k]}
                className={`text-left bg-white border-l-4 ${st.border} rounded-md p-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all ${molStateFilter === k && tab === "molecules" ? "ring-2 ring-slate-900" : ""}`}
              >
                <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold ${st.text} mb-1`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
                  {st.label}
                </div>
                <div className="text-[10px] font-mono-num text-slate-500 mb-2" title={STATE_HELP[k]}>
                  {STATE_RANGE[k]}
                </div>
                <div className="font-display text-xl font-bold tracking-tight font-mono-num">{item.molecules}</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">moléculas</div>
                <div className="mt-2 text-[11px] font-mono-num text-slate-600 leading-tight">
                  <div>{fmtEur((item.marca_euros || 0) + (item.generico_euros || 0))}</div>
                  <div className="text-slate-400">{fmtEur(item.marca_euros || 0)} m · {fmtEur(item.generico_euros || 0)} g</div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="text-[11px] text-slate-400 mt-2 italic">
          % cambio = unidades genéricas ÷ total EFG de la molécula (marca + genérico). Pasa el ratón sobre cada tarjeta para ver el rango y la explicación.
        </div>
      </div>

      {/* Color buckets (semáforo u/mes - filtro complementario) */}
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">
        Semáforo u/mes · referencia genérica
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <ColorCard color="green" units={s.color_units.green} euros={s.color_euros.green} count={s.color_counts.green} thresholds={data.thresholds} onOpen={() => setColorList("green")} />
        <ColorCard color="yellow" units={s.color_units.yellow} euros={s.color_euros.yellow} count={s.color_counts.yellow} thresholds={data.thresholds} onOpen={() => setColorList("yellow")} />
        <ColorCard color="red" units={s.color_units.red} euros={s.color_euros.red} count={s.color_counts.red} thresholds={data.thresholds} onOpen={() => setColorList("red")} />
      </div>
      {colorList && (
        <ColorItemsModal
          color={colorList}
          items={(data.items.generico || []).filter((i) => i.color === colorList)}
          thresholds={data.thresholds}
          months={data.months}
          molMap={molMap}
          onClose={() => setColorList(null)}
        />
      )}

      <StudyCnSearch
        items={data.items}
        onGoTo={(it) => {
          setTab(it._kind === "marca" ? "marca" : "overview");
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="${it._kind === "marca" ? "marca" : "generic"}-row-${it.cn}"]`);
            if (!el) return;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.classList.add("ring-2", "ring-sky-500", "bg-sky-50");
            setTimeout(() => el.classList.remove("ring-2", "ring-sky-500", "bg-sky-50"), 3000);
          }, 300);
        }}
      />

      {/* Tabs — grandes y visuales (iter-95) */}
      <div className="flex flex-wrap gap-2 mb-6 p-1.5 bg-slate-100/80 rounded-2xl" data-testid="detail-tabs">
        {[
          { id: "overview", label: "Genéricos · Semáforo", icon: Gauge, count: null },
          { id: "molecules", label: "Moléculas", icon: Layers, count: data.molecules.length },
          { id: "variability", label: "Variabilidad", icon: TrendingUp, count: null },
          { id: "top", label: "Top 25", icon: Trophy, count: null },
          { id: "marca", label: "Marca", icon: Star, count: s.marca_lines },
          { id: "suppliers", label: "Proveedores", icon: Truck, count: data.suppliers.length },
        ].map((t) => {
          const active = tab === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`group flex items-center gap-2.5 px-5 py-3.5 rounded-xl text-base font-semibold transition-all duration-200 ${
                active
                  ? "bg-slate-900 text-white shadow-lg shadow-slate-900/20 scale-[1.02]"
                  : "bg-white/70 text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm"
              }`}
            >
              <Icon className={`h-5 w-5 ${active ? "text-white" : "text-slate-400 group-hover:text-slate-700"}`} strokeWidth={1.8} />
              <span>{t.label}</span>
              {t.count != null && (
                <span
                  className={`font-mono-num text-xs px-2 py-0.5 rounded-full ${
                    active ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"
                  }`}
                >
                  {fmtNum(t.count)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <GenericTable id={id} items={data.items.generico} molMap={molMap} stockMeta={data.stock_meta} />}
      {tab === "molecules" && (
        <MoleculesTab
          id={id}
          data={data}
          reload={reload}
          stateFilter={molStateFilter}
          setStateFilter={setMolStateFilter}
          labFilter={molLabFilter}
          setLabFilter={setMolLabFilter}
          autoOnly={molAutoOnly}
          setAutoOnly={setMolAutoOnly}
          stockMeta={data.stock_meta}
        />
      )}
      {tab === "variability" && <VariabilityTab molecules={data.molecules} />}
      {tab === "top" && <TopTab id={id} data={data} molMap={molMap} stockMeta={data.stock_meta} />}
      {tab === "marca" && <MarcaTable id={id} items={data.items.marca} molMap={molMap} stockMeta={data.stock_meta} brandDiscountLabs={brandDiscountLabs} />}
      {tab === "suppliers" && (
        <SuppliersTab
          id={id}
          data={data}
          labs={labs}
          labsCatalog={labsCatalog}
          reload={reload}
          onFocusMolecules={focusMoleculesByLab}
          initialOpenSup={searchParams.get("lab") || null}
        />
      )}
    </div>
  );
}

/* ---------- KPI / Bar / Color ---------- */

function Kpi({ label, value, sub, accent, hint }) {
  return (
    <div
      className={`rounded-lg p-6 border ${accent ? accent + " border-slate-950" : "bg-white border-slate-200"}`}
      title={hint || undefined}
    >
      <div className={`text-xs uppercase tracking-[0.2em] font-semibold mb-3 inline-flex items-center gap-1.5 ${accent ? "text-slate-300" : "text-slate-500"}`}>
        {label}
        {hint && (
          <span
            className={`inline-flex items-center justify-center h-3.5 w-3.5 rounded-full text-[9px] font-bold cursor-help ${accent ? "bg-slate-700 text-slate-200" : "bg-slate-200 text-slate-600"}`}
            title={hint}
            aria-label={hint}
          >?</span>
        )}
      </div>
      <div className="font-display text-3xl font-bold tracking-tighter font-mono-num">{value}</div>
      {sub && <div className={`text-xs mt-1 font-mono-num ${accent ? "text-slate-300" : "text-slate-500"}`}>{sub}</div>}
    </div>
  );
}

function BarRow({ label, marca, generico, marcaPct, genericoPct, fmt }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</span>
        <span className="font-mono-num text-slate-600">
          <span className="font-semibold text-slate-900">{fmt(marca)}</span> · marca {marcaPct}% ·{" "}
          <span className="font-semibold text-blue-700">{fmt(generico)}</span> · gen {genericoPct}%
        </span>
      </div>
      <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden flex">
        <div className="bg-slate-900 h-full" style={{ width: `${marcaPct}%` }} />
        <div className="bg-blue-500 h-full" style={{ width: `${genericoPct}%` }} />
      </div>
    </div>
  );
}

function ColorCard({ color, units, euros, count, thresholds, onOpen }) {
  const c = COLOR[color];
  const range =
    color === "green" ? `≥ ${thresholds.green}` :
    color === "yellow" ? `${thresholds.yellow}–${thresholds.green - 1}` :
    `< ${thresholds.yellow}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`color-card-${color}`}
      className="text-left w-full bg-white border border-slate-200 rounded-lg p-6 hover:border-slate-400 hover:shadow-md transition-all group"
      title={`Ver las ${count} referencias de dispensación ${c.label.toLowerCase()}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${c.dot}`} />
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
            Dispensación {c.label}
          </span>
        </div>
        <span className="text-xs font-mono-num text-slate-400">{range} u/mes</span>
      </div>
      <div className="font-display text-3xl font-bold tracking-tighter font-mono-num">
        {fmtNum(units)} <span className="text-base text-slate-400 font-normal">u</span>
      </div>
      <div className="text-sm text-slate-700 mt-1 font-mono-num font-semibold">{fmtEur(euros)}</div>
      <div className="text-xs text-slate-500 mt-0.5 font-mono-num flex items-center gap-1">
        {count} referencias
        <span className="text-slate-400 group-hover:text-slate-900 transition-colors">· ver listado →</span>
      </div>
    </button>
  );
}

function ColorItemsModal({ color, items, thresholds, months, molMap, onClose }) {
  const c = COLOR[color];
  const [q, setQ] = useState("");
  const m = Math.max(1, Number(months) || 1);
  const rows = (items || [])
    .filter((i) => {
      if (!q.trim()) return true;
      const t = q.trim().toLowerCase();
      return (i.name || "").toLowerCase().includes(t) || (i.cn || "").includes(t) || (i.gh || "").toLowerCase().includes(t);
    })
    .sort((a, b) => (b.euros || 0) - (a.euros || 0));
  const sort = useTableSort(rows, {
    gh: (i) => i.gh || molMap?.[i.cn]?.gh,
    per_month: (i) => (i.units || 0) / m,
  });
  const range =
    color === "green" ? `≥ ${thresholds.green}` :
    color === "yellow" ? `${thresholds.yellow}–${thresholds.green - 1}` :
    `< ${thresholds.yellow}`;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid={`color-items-modal-${color}`}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${c.dot}`} /> Semáforo u/mes · {range} u/mes
            </div>
            <div className="mt-1 font-display text-2xl font-bold tracking-tight">
              Dispensación {c.label} · {rows.length} referencias genéricas
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100" data-testid={`color-items-close-${color}`}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 pt-4">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por CN, producto o molécula…"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid={`color-items-search-${color}`}
          />
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b-2 border-slate-200">
                <SortTh field="cn" sort={sort} className="py-2">CN</SortTh>
                <SortTh field="name" sort={sort} className="py-2">Producto</SortTh>
                <SortTh field="gh" sort={sort} className="py-2">Molécula (G.H.)</SortTh>
                <SortTh field="units" sort={sort} align="right" className="py-2">Uds</SortTh>
                <SortTh field="per_month" sort={sort} align="right" className="py-2">u/mes</SortTh>
                <SortTh field="euros" sort={sort} align="right" className="py-2">€</SortTh>
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((i) => (
                <tr key={i.cn} className="border-b border-slate-100" data-testid={`color-item-${i.cn}`}>
                  <td className="py-2 font-mono-num text-xs text-slate-500">{i.cn}</td>
                  <td className="py-2 max-w-[280px]"><span className="block truncate" title={i.name}>{i.name}</span></td>
                  <td className="py-2 max-w-[220px] text-slate-500">
                    <span className="block truncate" title={i.gh || molMap?.[i.cn]?.gh || ""}>{i.gh || molMap?.[i.cn]?.gh || "—"}</span>
                  </td>
                  <td className="py-2 text-right font-mono-num font-semibold">{fmtNum(i.units)}</td>
                  <td className="py-2 text-right font-mono-num">{(i.units / m).toFixed(1)}</td>
                  <td className="py-2 text-right font-mono-num">{fmtEur(i.euros)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <div className="text-sm text-slate-500 py-6 text-center">Sin referencias en este tramo.</div>}
        </div>
      </div>
    </div>
  );
}

/* ---------- ExpandedDetail (G.H. siblings) ---------- */

function MoleculeBenchmarkLine({ gh, myPct }) {
  const [bench, setBench] = useState(null);

  useEffect(() => {
    if (!gh) return;
    let cancelled = false;
    api
      .get("/benchmark/molecule", { params: { gh } })
      .then((r) => { if (!cancelled) setBench(r.data); })
      .catch(() => { if (!cancelled) setBench({ national: null, province: null }); });
    return () => { cancelled = true; };
  }, [gh]);

  if (!bench) {
    return <div className="mb-4 text-xs text-slate-400" data-testid="molecule-benchmark-loading">Cargando medias de mercado…</div>;
  }
  const nat = bench.national;
  const prov = bench.province;
  const ccaa = bench.ccaa;
  if (!nat && !prov && !ccaa) {
    return (
      <div className="mb-4 text-xs text-slate-500 bg-white border border-slate-200 rounded-md px-3 py-2" data-testid="molecule-benchmark-empty">
        Todavía no hay datos suficientes de otras farmacias para esta molécula (se necesitan al menos {bench.min_pharmacies || 3}).
      </div>
    );
  }
  const mine = Number(myPct || 0);
  const chip = (label, pct, sub, testid) => {
    const diff = mine - pct;
    const good = diff >= 0;
    return (
      <div className="flex-1 min-w-[170px] bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid={testid}>
        <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500">{label}</div>
        <div className="flex items-baseline gap-2">
          <span className="font-display text-xl font-bold font-mono-num">{pct.toFixed(1)}%</span>
          <span className={`text-[11px] font-mono-num font-bold ${good ? "text-emerald-600" : "text-rose-600"}`}>
            {good ? "+" : ""}{diff.toFixed(1)} pp tú
          </span>
        </div>
        <div className="text-[10px] text-slate-400 font-mono-num">{sub}</div>
      </div>
    );
  };
  return (
    <div className="mb-4 flex flex-wrap items-stretch gap-3" data-testid="molecule-benchmark">
      <div className="flex-1 min-w-[170px] bg-slate-900 text-white rounded-lg px-3 py-2">
        <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-300">Tu farmacia</div>
        <div className="font-display text-xl font-bold font-mono-num">{mine.toFixed(1)}%</div>
        <div className="text-[10px] text-slate-400 font-mono-num">% cambio a genérico</div>
      </div>
      {nat && chip("Media nacional", nat.pct_generic, `${nat.pharmacies} farmacias · ${nat.units} uds`, "molecule-benchmark-national")}
      {ccaa && chip(`Media ${bench.ccaa_name}`, ccaa.pct_generic, `${ccaa.pharmacies} farmacias · ${ccaa.units} uds`, "molecule-benchmark-ccaa")}
      {prov && chip(`Media ${bench.province_name}`, prov.pct_generic, `${prov.pharmacies} farmacias · ${prov.units} uds`, "molecule-benchmark-province")}
    </div>
  );
}

/* ---------- Evolución de la molécula en la farmacia (iter-98) ---------- */

function MoleculeHistoryPanel({ gh }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [natPct, setNatPct] = useState(null);

  const load = async () => {
    setOpen(true);
    if (data || loading) return;
    setLoading(true);
    try {
      const [ev, bm] = await Promise.all([
        api.get("/molecules/evolution", { params: { gh, limit: 3 } }),
        api.get("/benchmark/molecule", { params: { gh } }).catch(() => null),
      ]);
      setData(ev.data);
      setNatPct(bm?.data?.national?.pct_generic ?? null);
    } catch {
      setData({ points: [], delta_pp: 0 });
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <div className="mb-4">
        <button
          type="button"
          onClick={load}
          data-testid={`btn-molecule-history-${gh}`}
          className="inline-flex items-center gap-2 text-xs px-3 py-1.5 border border-slate-300 rounded-full bg-white hover:bg-slate-100 text-slate-700"
          title="Ver la evolución de esta molécula en los últimos meses"
        >
          <TrendingUp className="h-3 w-3" />
          Ver evolución (últimos 3 meses)
        </button>
      </div>
    );
  }

  const points = data?.points || [];
  const delta = data?.delta_pp ?? 0;
  const chartData = points.map((p) => ({ name: p.label, pct: p.pct }));

  return (
    <div className="mb-4 bg-white border border-slate-200 rounded-lg p-3" data-testid={`molecule-history-${gh}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500">
          Evolución · % cambio a genérico
        </div>
        <div className="flex items-center gap-3">
          {points.length > 1 && (
            <span
              className={`text-xs font-bold font-mono-num ${delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}
              data-testid={`molecule-history-delta-${gh}`}
            >
              {delta >= 0 ? "+" : ""}{delta.toFixed(1)} pp vs primer mes
            </span>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-[11px] text-slate-500 hover:text-slate-900"
            data-testid={`btn-molecule-history-close-${gh}`}
          >
            Ocultar
          </button>
        </div>
      </div>

      {loading && <div className="text-xs text-slate-400 py-6 text-center">Cargando evolución…</div>}

      {!loading && points.length === 0 && (
        <div className="text-xs text-slate-500 py-5 text-center">
          Todavía no hay estudios anteriores con esta molécula.
        </div>
      )}

      {!loading && points.length > 0 && (
        <>
          <div style={{ height: 190 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => `${v}%`} />
                {natPct != null && (
                  <ReferenceLine
                    y={natPct}
                    stroke="#0ea5e9"
                    strokeDasharray="4 4"
                    label={{ value: `Media nac. ${natPct}%`, position: "insideTopRight", fontSize: 10, fill: "#0284c7" }}
                  />
                )}
                <Line type="monotone" dataKey="pct" name="Tu farmacia" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs" data-testid={`molecule-history-table-${gh}`}>
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-1.5">Estudio</th>
                  <th className="text-right py-1.5">Marca u.</th>
                  <th className="text-right py-1.5">Gen. u.</th>
                  <th className="text-right py-1.5">% cambio</th>
                  <th className="text-right py-1.5">Marca €</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {points.map((p) => (
                  <tr key={p.disp_id}>
                    <td className="py-1.5 max-w-[180px]"><span className="block truncate" title={p.label}>{p.label}</span></td>
                    <td className="py-1.5 text-right font-mono-num">{fmtNum(p.marca_units)}</td>
                    <td className="py-1.5 text-right font-mono-num text-blue-700">{fmtNum(p.gen_units)}</td>
                    <td className="py-1.5 text-right font-mono-num font-semibold">{p.pct.toFixed(1)}%</td>
                    <td className="py-1.5 text-right font-mono-num">{fmtEur(p.marca_euros)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function GhDetail({ molecule, stockMeta, brandDiscountLabs }) {
  const [showOthers, setShowOthers] = useState(false);
  const [openLab, setOpenLab] = useState(null);
  if (!molecule) return null;
  const products = molecule.products || [];
  const marcaProds = products.filter((p) => p.type === "marca");
  const genProds = products.filter((p) => p.type === "generico");
  const dispensedLabs = new Set(products.map((p) => (p.laboratorio || "").trim()).filter(Boolean));
  const labDetail = molecule.available_labs_detail || [];
  const otherLabs = labDetail.filter((l) => !dispensedLabs.has(l.laboratorio));
  const showStock = !!stockMeta?.has_stock;

  // Aviso ámbar: si la molécula está en Solo Marca / Marginal, no dispensamos genérico y SI tenemos stock de otro lab EFG
  const isFocus = molecule.state_key === "solo_marca" || molecule.state_key === "marginal";
  const stockedOtherLabs = showStock
    ? labDetail.filter((l) => l.products.some((p) => (p.stock_units || 0) > 0))
    : [];
  const showOtherLabAlert = isFocus && stockedOtherLabs.length > 0;

  return (
    <div className="bg-slate-50 border-y border-slate-200 px-6 py-5">
      {showOtherLabAlert && (
        <div className="mb-4 bg-amber-50 border border-amber-300 rounded-md p-3 inline-flex items-start gap-2 text-amber-900" data-testid="stock-other-lab-alert">
          <span className="text-amber-600 mt-0.5">⚠</span>
          <div className="text-xs">
            <strong>Tienes stock de otro laboratorio EFG</strong> para esta molécula:&nbsp;
            {stockedOtherLabs.map((l) => l.laboratorio).join(" · ")}
            . Decide si lo dispensas antes de pedir el preferente.
          </div>
        </div>
      )}
      <MoleculeBenchmarkLine gh={molecule.gh} myPct={molecule.generic_share_pct} />
      <MoleculeHistoryPanel gh={molecule.gh} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ProductBlock title="Marca" color="bg-slate-900" items={marcaProds} showStock={showStock} brandDiscountLabs={brandDiscountLabs} genericoPvl={molecule?.gh_pvl} />
        <ProductBlock title="Genérico" color="bg-blue-500" items={genProds} showStock={showStock} brandDiscountLabs={brandDiscountLabs} />
      </div>
      <div className="mt-5 pt-4 border-t border-slate-200">
        <button
          onClick={() => setShowOthers(!showOthers)}
          className="inline-flex items-center gap-2 text-xs px-3 py-1.5 border border-slate-300 rounded-full bg-white hover:bg-slate-100 text-slate-700"
          data-testid="see-other-labs"
        >
          <Building2 className="h-3 w-3" />
          {showOthers ? "Ocultar" : "¿Quieres ver quién más la tiene?"}
          {otherLabs.length > 0 && (
            <span className="text-[10px] font-mono-num bg-slate-100 px-2 py-0.5 rounded-full text-slate-600">{otherLabs.length}</span>
          )}
        </button>
        {showOthers && (
          <div className="mt-3 space-y-2" data-testid="other-labs-list">
            {otherLabs.length === 0 ? (
              <div className="text-sm text-slate-500 italic bg-white border border-slate-200 rounded-md p-3">
                {labDetail.length === 0
                  ? "No tienes en tu vademécum laboratorios con genérico EFG para esta molécula."
                  : "Todos los laboratorios de tu vademécum para esta molécula ya están dispensando."}
              </div>
            ) : (
              <>
                <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">
                  Otros laboratorios con genérico EFG ({otherLabs.length}) — clic para ver el CN
                </div>
                {otherLabs.map((lab) => {
                  const isOpen = openLab === lab.laboratorio;
                  const labStockTotal = lab.products.reduce((s, p) => s + (p.stock_units || 0), 0);
                  const hasStock = showStock && labStockTotal > 0;
                  return (
                    <div key={lab.laboratorio} className={`bg-white border rounded-md ${hasStock ? "border-amber-300" : "border-slate-200"}`}>
                      <button
                        onClick={() => setOpenLab(isOpen ? null : lab.laboratorio)}
                        className="w-full px-3 py-2 flex items-center justify-between hover:bg-slate-50"
                        data-testid={`other-lab-${lab.laboratorio}`}
                      >
                        <span className="text-sm font-medium text-slate-900 flex items-center gap-2">
                          {lab.laboratorio}
                          {hasStock && (
                            <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1">
                              ⚠ {labStockTotal} uds en stock
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-2 text-xs">
                          <span className="font-mono-num bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                            {lab.products.length} CN
                          </span>
                          {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="border-t border-slate-200 bg-slate-50/40 px-3 py-2">
                          <table className="w-full text-xs">
                            <tbody className="divide-y divide-slate-100">
                              {lab.products.map((p) => {
                                const st = p.stock_units;
                                return (
                                  <tr key={p.cn} className={showStock && st > 0 ? "bg-amber-50/50" : ""}>
                                    <td className="py-1.5 font-mono-num text-slate-500 w-20">{p.cn}</td>
                                    <td className="py-1.5">{p.name || "—"}</td>
                                    {showStock && (
                                      <td className="py-1.5 text-right font-mono-num text-xs w-20">
                                        {st > 0 ? (
                                          <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full font-bold">{st} uds</span>
                                        ) : (
                                          <span className="text-slate-400">0</span>
                                        )}
                                      </td>
                                    )}
                                    <td className="py-1.5 text-right font-mono-num text-slate-600 w-20">
                                      {p.pvl != null ? fmtEur(p.pvl) : "—"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* Iter-63: chip pequeño con estrella para marcas cuyo laboratorio tiene un
   descuento extra negociado (brand_discount_pct > 0). Se ve en la tabla de
   Marca y en el desplegable "Marca" dentro de la expansión de G.H. */
function BrandExtraStar({ laboratorio, brandDiscountLabs, cn, pvl, genericoPvl }) {
  const [open, setOpen] = useState(false);
  const [lookup, setLookup] = useState(null); // {laboratorio, pvl, discount_pct, lab_default_pct}
  const [loading, setLoading] = useState(false);
  if (!laboratorio || !brandDiscountLabs) return null;
  const norm = String(laboratorio).trim().toUpperCase();
  if (!brandDiscountLabs.has(norm)) return null;

  const handleClick = async (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (!lookup && cn) {
      setLoading(true);
      try {
        const { data } = await api.get(`/brand-discounts/lookup-cn?cn=${encodeURIComponent(cn)}`);
        // Prefiere el match cuyo laboratorio coincida (upper) con el actual
        const matches = data?.matches || [];
        const preferred = matches.find((m) => String(m.laboratorio || "").trim().toUpperCase() === norm) || matches[0] || null;
        setLookup(preferred);
      } catch { /* silent */ } finally { setLoading(false); }
    }
  };

  // Cálculo del neto vs genérico (aprox, en €/uds):
  //   marca_pvl_con_dto = pvl × (1 - dto/100)
  //   ahorro_por_uds vs marca sin dto = pvl × dto/100
  //   neto_vs_generico = generico_pvl - marca_pvl_con_dto (si > 0 → sale más barato el genérico incluso con el dto; si < 0 → merece la pena la marca)
  const pct = lookup?.discount_pct ?? 0;
  const effectivePvl = pvl != null ? Number(pvl) * (1 - pct / 100) : null;
  const savingPerUnit = pvl != null ? Number(pvl) * pct / 100 : null;
  const netVsGeneric = (effectivePvl != null && genericoPvl != null) ? (Number(genericoPvl) - effectivePvl) : null;

  return (
    <span className="relative inline-flex shrink-0">
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider bg-violet-100 hover:bg-violet-200 text-violet-800 border border-violet-300 px-1.5 py-0.5 rounded-full font-bold cursor-pointer transition-colors"
        title="Click para ver el descuento negociado con este laboratorio"
        data-testid={`brand-extra-star-${laboratorio}`}
      >
        <Star className="h-2.5 w-2.5 fill-violet-500 stroke-violet-500" />
        Dto extra
      </button>
      {open && (
        <div
          className="absolute z-50 top-full left-0 mt-1 w-72 bg-white border border-violet-300 rounded-lg shadow-xl p-3 text-left"
          onClick={(e) => e.stopPropagation()}
          data-testid={`brand-extra-popover-${cn || laboratorio}`}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <div className="text-[9px] uppercase tracking-wider font-bold text-violet-600">Descuento negociado</div>
              <div className="font-display text-sm font-bold text-slate-900 leading-tight">{laboratorio}</div>
              {cn && <div className="text-[10px] text-slate-500 font-mono-num">CN {cn}</div>}
            </div>
            <button type="button" onClick={() => setOpen(false)} className="p-0.5 rounded hover:bg-slate-100">
              <X className="h-3 w-3 text-slate-500" />
            </button>
          </div>
          {loading ? (
            <div className="text-[11px] text-slate-500 italic py-2">Consultando…</div>
          ) : !lookup ? (
            <div className="text-[11px] text-slate-500 italic py-2">
              Este CN no está en el escalado subido. FarmaClass usa el % general del lab si está configurado.
            </div>
          ) : (
            <div className="space-y-1.5 text-[11px]">
              <div className="flex justify-between"><span className="text-slate-500">PVL</span><span className="font-mono-num font-bold text-slate-900">{fmtEur(pvl ?? lookup.pvl)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Dto negociado</span><span className="font-mono-num font-bold text-violet-700">{pct.toFixed(1)}%</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Precio con dto</span><span className="font-mono-num font-bold text-slate-900">{fmtEur(effectivePvl)}</span></div>
              <div className="flex justify-between border-t border-slate-100 pt-1.5"><span className="text-slate-500">Ahorro por uds</span><span className="font-mono-num font-bold text-emerald-700">{fmtEur(savingPerUnit)}</span></div>
              {genericoPvl != null && (
                <div className="flex justify-between bg-slate-50 -mx-3 px-3 py-1.5 mt-1 border-t border-slate-200">
                  <span className="text-slate-600 font-semibold">Neto vs genérico</span>
                  <span className={`font-mono-num font-bold ${netVsGeneric != null && netVsGeneric < 0 ? "text-emerald-700" : "text-amber-700"}`}>
                    {netVsGeneric != null ? (netVsGeneric < 0 ? "−" : "+") : ""}{netVsGeneric != null ? fmtEur(Math.abs(netVsGeneric)) : "—"}
                  </span>
                </div>
              )}
              <div className="text-[10px] text-slate-400 italic pt-1">
                {netVsGeneric != null && netVsGeneric < 0
                  ? "✓ Compensa quedarse con la marca (más barata que el genérico)."
                  : netVsGeneric != null && netVsGeneric >= 0
                    ? "El genérico sigue siendo más económico."
                    : "Configura el PVL conjunto del G.H. para ver el neto vs genérico."}
              </div>
            </div>
          )}
        </div>
      )}
    </span>
  );
}

function ProductBlock({ title, color, items, showStock, brandDiscountLabs, genericoPvl }) {
  if (!items.length)
    return (
      <div>
        <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2 flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${color}`} /> {title}
        </div>
        <div className="text-sm text-slate-400 italic">Sin movimiento de {title.toLowerCase()} en este conjunto.</div>
      </div>
    );
  const totalUds = items.reduce((s, p) => s + (p.units || 0), 0);
  return (
    <div data-testid={`product-block-${title.toLowerCase()}`}>
      <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2 flex items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${color}`} /> {title} ({items.length})
        </span>
        <span className="font-mono-num normal-case tracking-normal text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
          {fmtNum(totalUds)} uds
        </span>
      </div>
      <div className="bg-white border border-slate-200 rounded divide-y divide-slate-100">
        {items.map((p) => (
          <div key={p.cn} className="px-2 py-1.5 text-xs" data-testid={`product-row-${p.cn}`}>
            {/* Descripción + laboratorio en línea completa (visibles en contenedores estrechos) */}
            {(p.name || p.gh) && (
              <div
                className="truncate font-medium text-slate-900"
                title={p.name || p.gh}
                data-testid={`product-desc-${p.cn}`}
              >
                {p.name || p.gh}
              </div>
            )}
            {p.laboratorio && (
              <div className="flex items-center gap-1.5 mb-0.5">
                <div className="truncate text-[10px] text-slate-500" title={p.laboratorio}>
                  {p.laboratorio}
                </div>
                {title === "Marca" && (
                  <BrandExtraStar laboratorio={p.laboratorio} brandDiscountLabs={brandDiscountLabs} cn={p.cn} pvl={p.pvl} genericoPvl={genericoPvl} />
                )}
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="font-mono-num text-slate-500 w-16 shrink-0">{p.cn}</span>
              <div className="flex-1 min-w-0" />
              <span className="font-mono-num font-bold bg-slate-900 text-white px-2 py-0.5 rounded-full whitespace-nowrap text-[11px]" title={`${p.units} uds dispensadas`}>
                {fmtNum(p.units)} uds
              </span>
              {showStock && (
                <span className="font-mono-num text-[10px] whitespace-nowrap w-16 text-right">
                  {p.stock_units > 0 ? (
                    <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold" title={`Stock: ${p.stock_units} uds`}>
                      Stock {p.stock_units}
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </span>
              )}
              <span className="font-mono-num font-semibold text-slate-900 whitespace-nowrap text-right w-20">{fmtEur(p.euros)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Genéricos · Semáforo (con marca al lado, expandible) ---------- */

function GenericTable({ id, items, molMap, stockMeta }) {
  const [open, setOpen] = useState({});
  const [stateFilter, setStateFilter] = useState("");

  // Enrich items with sortable derived fields (molecule numbers + pvl_conj)
  const enriched = useMemo(() => items.map((it) => {
    const mol = molMap[it.gh] || {};
    return {
      ...it,
      pvl_conj: mol.gh_pvl ?? null,
      mol_marca_units: mol.marca_units ?? 0,
      mol_marca_euros: mol.marca_euros ?? 0,
    };
  }), [items, molMap]);

  const filtered = useMemo(() => {
    if (!stateFilter) return enriched;
    return enriched.filter((i) => i.state_key === stateFilter);
  }, [enriched, stateFilter]);

  const { sorted, sortKey, direction, toggle } = useSortable(filtered, "name", "asc", "disp-generic-v2");

  // Agrupado por familia de principio activo cuando se ordena alfabéticamente.
  // Atípico = % cambio (% genérico del G.H.) muy por debajo de la mediana de la familia.
  const groups = useMemo(() => {
    if (sortKey !== "name") return null;
    return buildMoleculeGroups(
      sorted,
      (i) => i.gh || i.name,
      (i) => (typeof i.mol_change_pct === "number" ? i.mol_change_pct : null),
    );
  }, [sorted, sortKey]);

  if (!items.length) return <Empty msg="Sin genéricos en esta dispensación." />;
  return (
    <div className="space-y-3">
      <SectionExport id={id} section="generic" />
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap gap-2 items-center">
        <span className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mr-2">Filtrar por estado:</span>
        <button onClick={() => setStateFilter("")} className={`text-xs px-3 py-1 rounded-full border ${stateFilter === "" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          Todos ({items.length})
        </button>
        {Object.keys(STATE).filter((k) => k !== "no_data").map((k) => {
          const cnt = items.filter((i) => i.state_key === k).length;
          if (!cnt) return null;
          const st = STATE[k];
          return (
            <button
              key={k}
              onClick={() => setStateFilter(stateFilter === k ? "" : k)}
              data-testid={`filter-state-${k}`}
              className={`text-xs px-3 py-1 rounded-full border inline-flex items-center gap-1.5 ${stateFilter === k ? `${st.bg} ${st.text} border-current font-semibold` : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
              {st.label} ({cnt})
            </button>
          );
        })}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="generic-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-[0.15em] text-slate-500">
            <tr>
              <th className="p-3 w-8"></th>
              <th className="p-3 w-8"></th>
              <SortableTh k="cn" sortKey={sortKey} direction={direction} toggle={toggle}>CN</SortableTh>
              <SortableTh k="name" sortKey={sortKey} direction={direction} toggle={toggle}>Producto · G.H.</SortableTh>
              <SortableTh k="pvl_conj" sortKey={sortKey} direction={direction} toggle={toggle} align="right">PVL conj.</SortableTh>
              <SortableTh k="mol_marca_units" sortKey={sortKey} direction={direction} toggle={toggle} align="right" className="bg-slate-100">Marca u.</SortableTh>
              <SortableTh k="mol_marca_euros" sortKey={sortKey} direction={direction} toggle={toggle} align="right" className="bg-slate-100">Marca €</SortableTh>
              <SortableTh k="units" sortKey={sortKey} direction={direction} toggle={toggle} align="right" className="bg-blue-50">Gen. u.</SortableTh>
              <SortableTh k="euros" sortKey={sortKey} direction={direction} toggle={toggle} align="right" className="bg-blue-50">Gen. €</SortableTh>
              <SortableTh k="mol_change_pct" sortKey={sortKey} direction={direction} toggle={toggle} align="right">% camb.</SortableTh>
              <SortableTh k="state_key" sortKey={sortKey} direction={direction} toggle={toggle}>Estado</SortableTh>
              <SortableTh k="units_per_month" sortKey={sortKey} direction={direction} toggle={toggle}>u/mes</SortableTh>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {(groups || [{ root: "", items: sorted, isGroup: false, avgPct: null }]).map((g, gi) => (
              <FragmentRow key={`grp-${gi}-${g.root}`}>
                {g.isGroup && (
                  <tr className="bg-violet-50/70" data-testid={`generic-group-${g.root}`}>
                    <td colSpan={12} className="px-3 py-2 border-l-4 border-violet-400">
                      <div className="flex items-center justify-between gap-3 flex-wrap text-xs">
                        <div className="font-bold text-violet-900 uppercase tracking-wider flex items-center gap-2">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500" />
                          Grupo · {g.root}
                          <span className="text-violet-700/70 font-normal normal-case tracking-normal">
                            ({g.items.length} presentaciones)
                          </span>
                        </div>
                        {g.medianPct != null && (
                          <div className="text-violet-800">
                            Mediana % gen. del grupo: <strong className="font-mono-num">{g.medianPct.toFixed(1)}%</strong>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                {g.items.map((it) => {
                  const c = COLOR[it.color];
                  const mol = molMap[it.gh] || null;
                  const isOpen = !!open[it.cn];
                  const atypical = it._isAtypical;
                  return (
                    <FragmentRow key={it.cn}>
                      <tr
                        className={`hover:bg-slate-50 cursor-pointer ${atypical ? "bg-rose-50/60 ring-1 ring-rose-300/40" : ""}`}
                        onClick={() => setOpen({ ...open, [it.cn]: !isOpen })}
                        data-testid={`generic-row-${it.cn}`}
                      >
                        <td className={`p-3 ${g.isGroup ? "border-l-4 border-violet-400" : ""}`}>{mol ? (isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />) : null}</td>
                        <td className="p-3"><span className={`h-2.5 w-2.5 block rounded-full ${c.dot}`} title={`u/mes: ${c.label}`} /></td>
                        <td className="p-3 font-mono-num">{it.cn}</td>
                        <td className="p-3 max-w-md">
                          <div className="font-medium flex items-center gap-2 min-w-0">
                            <span className="truncate">{it.name || "—"}</span>
                            {atypical && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded-full font-bold border border-rose-300 whitespace-nowrap shrink-0"
                                title="% genérico significativamente POR DEBAJO de la mediana de la familia: oportunidad de mejora."
                                data-testid={`atypical-cn-${it.cn}`}
                              >
                                <AlertTriangle className="h-3 w-3" />
                                ATÍPICO ↓
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 truncate">{it.gh || "Sin G.H."} · {it.laboratorio || "—"}</div>
                        </td>
                        <td className="p-3 text-right font-mono-num text-slate-600">{it.pvl_conj != null ? fmtEur(it.pvl_conj) : "—"}</td>
                        <td className="p-3 text-right font-mono-num bg-slate-50">{mol ? fmtNum(mol.marca_units) : "—"}</td>
                        <td className="p-3 text-right font-mono-num bg-slate-50">{mol ? fmtEur(mol.marca_euros) : "—"}</td>
                        <td className="p-3 text-right font-mono-num font-semibold bg-blue-50/40">{fmtNum(it.units)}</td>
                        <td className="p-3 text-right font-mono-num font-semibold bg-blue-50/40">{fmtEur(it.euros)}</td>
                        <td className="p-3 text-right font-mono-num font-semibold">{it.mol_change_pct != null ? `${it.mol_change_pct}%` : "—"}</td>
                        <td className="p-3">{it.state_key ? <StateBadge stateKey={it.state_key} /> : "—"}</td>
                        <td className="p-3 text-xs text-slate-500 font-mono-num">
                          {Number(it.units_per_month ?? 0).toLocaleString("es-ES", { maximumFractionDigits: 1 })} · {c.label}
                        </td>
                      </tr>
                      {isOpen && mol && (
                        <tr><td colSpan={12} className="p-0"><GhDetail molecule={mol} stockMeta={stockMeta} /></td></tr>
                      )}
                    </FragmentRow>
                  );
                })}
              </FragmentRow>
            ))}
          </tbody>
        </table>
        {sorted.length === 0 && (
          <div className="p-8 text-center text-slate-500">Sin resultados para este filtro.</div>
        )}
      </div>
    </div>
  );
}

const FragmentRow = ({ children }) => <>{children}</>;

/* ---------- Marca (con genérico del mismo G.H.) ---------- */

function MarcaTable({ id, items, molMap, stockMeta, brandDiscountLabs }) {
  const [open, setOpen] = useState({});
  const enriched = useMemo(() => items.map((it) => {
    const mol = molMap[it.gh] || {};
    return { ...it, pvl_conj: mol.gh_pvl ?? null, mol_generico_units: mol.generico_units ?? 0, mol_generico_euros: mol.generico_euros ?? 0 };
  }), [items, molMap]);
  const { sorted, sortKey, direction, toggle } = useSortable(enriched, "euros", "desc", "disp-marca");
  if (!items.length) return <Empty msg="Sin productos de marca EFG." />;
  return (
    <div className="space-y-3">
      <SectionExport id={id} section="marca" />
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="marca-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-[0.15em] text-slate-500">
            <tr>
              <th className="p-3 w-8"></th>
              <SortableTh k="cn" sortKey={sortKey} direction={direction} toggle={toggle}>CN</SortableTh>
              <SortableTh k="name" sortKey={sortKey} direction={direction} toggle={toggle}>Producto · G.H.</SortableTh>
              <SortableTh k="pvl_conj" sortKey={sortKey} direction={direction} toggle={toggle} align="right">PVL conj.</SortableTh>
              <SortableTh k="units" sortKey={sortKey} direction={direction} toggle={toggle} align="right" className="bg-slate-100">Marca u.</SortableTh>
              <SortableTh k="euros" sortKey={sortKey} direction={direction} toggle={toggle} align="right" className="bg-slate-100">Marca €</SortableTh>
              <SortableTh k="mol_generico_units" sortKey={sortKey} direction={direction} toggle={toggle} align="right" className="bg-blue-50">Gen. u.</SortableTh>
              <SortableTh k="mol_generico_euros" sortKey={sortKey} direction={direction} toggle={toggle} align="right" className="bg-blue-50">Gen. €</SortableTh>
              <SortableTh k="state_key" sortKey={sortKey} direction={direction} toggle={toggle}>Estado · molécula</SortableTh>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {sorted.map((it) => {
              const mol = molMap[it.gh] || null;
              const isOpen = !!open[it.cn];
              return (
                <FragmentRow key={it.cn}>
                  <tr className="hover:bg-slate-50 cursor-pointer" data-testid={`marca-row-${it.cn}`} onClick={() => setOpen({ ...open, [it.cn]: !isOpen })}>
                    <td className="p-3">{mol ? (isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />) : null}</td>
                    <td className="p-3 font-mono-num">{it.cn}</td>
                    <td className="p-3 max-w-md">
                      <div className="font-medium truncate">{it.name}</div>
                      <div className="text-xs text-slate-500 truncate flex items-center gap-1.5">
                        <span className="truncate">{it.gh || "Sin G.H."} · {it.laboratorio || "—"}</span>
                        <BrandExtraStar laboratorio={it.laboratorio} brandDiscountLabs={brandDiscountLabs} cn={it.cn} pvl={it.pvl} genericoPvl={it.pvl_conj} />
                      </div>
                    </td>
                    <td className="p-3 text-right font-mono-num text-slate-600">{it.pvl_conj != null ? fmtEur(it.pvl_conj) : "—"}</td>
                    <td className="p-3 text-right font-mono-num font-semibold bg-slate-50">{fmtNum(it.units)}</td>
                    <td className="p-3 text-right font-mono-num font-semibold bg-slate-50">{fmtEur(it.euros)}</td>
                    <td className="p-3 text-right font-mono-num bg-blue-50/40">{mol ? fmtNum(mol.generico_units) : "—"}</td>
                    <td className="p-3 text-right font-mono-num bg-blue-50/40">{mol ? fmtEur(mol.generico_euros) : "—"}</td>
                    <td className="p-3">{it.state_key ? <StateBadge stateKey={it.state_key} pct={it.mol_change_pct} /> : "—"}</td>
                  </tr>
                  {isOpen && mol && (
                    <tr><td colSpan={9} className="p-0"><GhDetail molecule={mol} stockMeta={stockMeta} brandDiscountLabs={brandDiscountLabs} /></td></tr>
                  )}
                </FragmentRow>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Moléculas ---------- */

function MoleculesTab({ id, data, reload, stateFilter, setStateFilter, labFilter, setLabFilter, autoOnly, setAutoOnly, stockMeta }) {
  const [empModalGh, setEmpModalGh] = useState(null);
  const [filter, setFilter] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("gh") || ""; } catch { return ""; }
  });
  const [prefByGh, setPrefByGh] = useState({});
  const [open, setOpen] = useState({});
  const [showBulk, setShowBulk] = useState(false);
  const [showBestLabs, setShowBestLabs] = useState(false);
  const [showCoverage, setShowCoverage] = useState(false);


  const autoAssignedCount = useMemo(
    () => data.molecules.filter((m) => m.preferred_lab_auto).length,
    [data.molecules],
  );

  const filtered = useMemo(() => {
    let arr = data.molecules.map((m) => ({ ...m, pvl_conj: m.gh_pvl ?? null }));
    if (stateFilter) arr = arr.filter((m) => m.state_key === stateFilter);
    if (autoOnly) arr = arr.filter((m) => m.preferred_lab_auto);
    if (labFilter) arr = arr.filter((m) => m.preferred_lab === labFilter);
    if (filter.trim()) {
      const q = filter.toLowerCase();
      arr = arr.filter((m) => m.gh.toLowerCase().includes(q));
    }
    return arr;
  }, [data, stateFilter, filter, autoOnly, labFilter]);

  const { sorted, sortKey, direction, toggle } = useSortable(filtered, "gh", "asc", "disp-molecules-v2");

  // Cuando el orden es alfabético por molécula, agrupamos G.H. de la misma familia
  // de principio activo (p.ej. todos los ALPRAZOLAM independientemente de dosis o
  // tamaño de envase) y marcamos como atípica la G.H. cuyo % gen está muy por
  // debajo de la mediana de las otras G.H. de la familia.
  const groups = useMemo(() => {
    if (sortKey !== "gh") return null;
    return buildMoleculeGroups(sorted, (m) => m.gh, (m) => m.generic_share_pct);
  }, [sorted, sortKey]);

  const savePref = async (gh) => {
    const lab = prefByGh[gh];
    if (!lab) {
      toast.error("Selecciona un laboratorio");
      return;
    }
    await api.post("/molecule-preferences", { gh, preferred_lab: lab });
    toast.success("Proveedor preferido asignado");
    reload();
  };

  const stateInfo = stateFilter ? STATE[stateFilter] : null;

  const renderMolRow = (m, inGroup) => {
    const isOpen = !!open[m.gh];
    const availLabs = m.available_labs && m.available_labs.length ? m.available_labs : null;
    const atypical = m._isAtypical;
    return (
      <FragmentRow key={m.gh}>
        <tr
          className={`hover:bg-slate-50 cursor-pointer ${atypical ? "bg-rose-50/60 ring-1 ring-rose-300/40" : ""}`}
          onClick={() => setOpen({ ...open, [m.gh]: !isOpen })}
          data-testid={`molecule-row-${encodeURIComponent(m.gh)}`}
        >
          <td className={`p-3 ${inGroup ? "border-l-4 border-violet-400" : ""}`}>
            {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
          </td>
          <td className="p-3">
            <div className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
              {m.gh}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setEmpModalGh(m.gh); }}
                data-testid={`btn-mol-employees-${encodeURIComponent(m.gh)}`}
                title="Ver oportunidades y % de cambio por empleado en esta molécula"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-slate-900 text-white hover:bg-slate-700 transition-colors"
              >
                <Users className="h-3 w-3" />
                Empleados
              </button>
              {atypical && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded-full font-bold border border-rose-300"
                  title="% genérico significativamente POR DEBAJO del resto del grupo: oportunidad clara de mejora."
                  data-testid={`atypical-${encodeURIComponent(m.gh)}`}
                >
                  <AlertTriangle className="h-3 w-3" />
                  ATÍPICO ↓
                </span>
              )}
            </div>
          </td>
          <td className="p-3 text-right font-mono-num text-slate-600">
            {m.gh_pvl != null ? fmtEur(m.gh_pvl) : "—"}
          </td>
          <td className="p-3 text-right font-mono-num bg-slate-50">{fmtNum(m.marca_units)}</td>
          <td className="p-3 text-right font-mono-num font-semibold bg-slate-50">{fmtEur(m.marca_euros)}</td>
          <td className="p-3 text-right font-mono-num bg-blue-50/40">{fmtNum(m.generico_units)}</td>
          <td className="p-3 text-right font-mono-num font-semibold bg-blue-50/40">{fmtEur(m.generico_euros)}</td>
          <td className="p-3 text-right font-mono-num font-semibold">{m.generic_share_pct}%</td>
          <td className="p-3"><StateBadge stateKey={m.state_key} /></td>
          <td className="p-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <select
                value={prefByGh[m.gh] ?? m.preferred_lab ?? ""}
                onChange={(e) => setPrefByGh({ ...prefByGh, [m.gh]: e.target.value })}
                className="text-xs px-2 py-1.5 border border-slate-200 rounded bg-white max-w-[180px]"
                disabled={!availLabs}
                title={!availLabs ? "Sin laboratorios con genérico de este G.H. en tu catálogo" : ""}
              >
                <option value="">— {availLabs ? "Ninguno" : "Sin opciones"} —</option>
                {(availLabs || []).map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
              {m.preferred_lab_auto && !prefByGh[m.gh] && (
                <span
                  className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider bg-violet-100 text-violet-800 px-1.5 py-0.5 rounded-full font-bold border border-violet-300"
                  title="Proveedor asignado automáticamente: laboratorio con condición que tiene más stock para este G.H. Pulsa el selector para sobrescribir."
                  data-testid={`auto-badge-${encodeURIComponent(m.gh)}`}
                >
                  AUTO
                </span>
              )}
              <button
                onClick={() => savePref(m.gh)}
                disabled={!availLabs}
                className="p-1.5 text-slate-500 hover:text-slate-900 disabled:text-slate-300"
                title="Guardar"
                data-testid={`save-pref-${encodeURIComponent(m.gh)}`}
              >
                <Save className="h-3.5 w-3.5" />
              </button>
            </div>
          </td>
        </tr>
        {isOpen && (
          <tr>
            <td colSpan={10} className="p-0">
              <GhDetail molecule={m} stockMeta={stockMeta} />
            </td>
          </tr>
        )}
      </FragmentRow>
    );
  };

  return (
    <div data-testid="molecules-tab">
      <SectionExport id={id} section="molecules" />
      {stateInfo && (
        <div className={`mb-4 rounded-lg border-l-4 ${stateInfo.border} ${stateInfo.bg} p-4 flex items-center justify-between gap-4`} data-testid="filter-title">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-1">Listado filtrado</div>
            <h2 className={`font-display text-2xl font-bold tracking-tight ${stateInfo.text}`}>{stateInfo.label}</h2>
            <p className="text-xs text-slate-600 mt-1 font-mono-num">
              {sorted.length} moléculas
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {stateFilter === "solo_marca" && sorted.length > 0 && (
              <>
                <button
                  onClick={() => setShowBestLabs(true)}
                  data-testid="open-best-labs-suggest"
                  className="inline-flex items-center gap-1.5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-3 py-2 rounded-md text-xs font-medium hover:from-emerald-500 hover:to-emerald-400 shadow-sm"
                  title="Asigna cada molécula al lab con MEJOR condición según lo que ya tienes cargado en Condiciones"
                >
                  <Sparkles className="h-3 w-3" />
                  ¿Te propongo la opción más rentable?
                </button>
                <button
                  onClick={() => setShowCoverage(true)}
                  data-testid="open-unassigned-coverage"
                  className="inline-flex items-center gap-1.5 bg-gradient-to-r from-sky-700 to-sky-600 text-white px-3 py-2 rounded-md text-xs font-medium hover:from-sky-600 hover:to-sky-500 shadow-sm"
                  title="Lista las moléculas sin proveedor asignado y te propone el laboratorio que cubre más"
                >
                  <Sparkles className="h-3 w-3" />
                  Sin proveedor: ¿quién cubre más?
                </button>
                <button
                  onClick={() => setShowBulk(!showBulk)}
                  data-testid="open-bulk-pref"
                  className="inline-flex items-center gap-1.5 bg-slate-950 text-white px-3 py-2 rounded-md text-xs font-medium hover:bg-slate-800"
                >
                  <ArrowRightLeft className="h-3 w-3" />
                  {showBulk ? "Cerrar" : "Asignar 3 proveedores"}
                </button>
                <button
                  onClick={() => downloadXlsx(`/dispensations/${id}/solo-marca-list?format=xlsx`, "solo_marca_listado.xlsx")}
                  data-testid="export-solo-marca-list-xlsx"
                  className="inline-flex items-center gap-1.5 bg-white border border-emerald-300 text-emerald-800 px-3 py-2 rounded-md text-xs font-medium hover:bg-emerald-50"
                  title="Listado plano por molécula (sin proveedor asignado — para trabajarlo en papel)"
                >
                  <Download className="h-3 w-3" />
                  Excel (listado)
                </button>
                <button
                  onClick={() => downloadXlsx(`/dispensations/${id}/solo-marca-list?format=pdf`, "solo_marca_listado.pdf")}
                  data-testid="export-solo-marca-list-pdf"
                  className="inline-flex items-center gap-1.5 bg-white border border-emerald-300 text-emerald-800 px-3 py-2 rounded-md text-xs font-medium hover:bg-emerald-50"
                  title="PDF plano por molécula (sin proveedor asignado — para trabajarlo en papel)"
                >
                  <FileText className="h-3 w-3" />
                  PDF (listado)
                </button>
                <button
                  onClick={() => downloadXlsx(`/dispensations/${id}/solo-marca-by-supplier?format=xlsx`, "solo_marca_proveedores.xlsx")}
                  data-testid="export-solo-marca-by-supplier-xlsx"
                  className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-900 px-3 py-2 rounded-md text-xs font-medium hover:bg-slate-50"
                  title="Listado por proveedor asignado (requiere asignar 3 proveedores primero)"
                >
                  <Download className="h-3 w-3" />
                  Excel por proveedor
                </button>
                <button
                  onClick={() => downloadXlsx(`/dispensations/${id}/solo-marca-by-supplier?format=pdf`, "solo_marca_proveedores.pdf")}
                  data-testid="export-solo-marca-by-supplier-pdf"
                  className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-900 px-3 py-2 rounded-md text-xs font-medium hover:bg-slate-50"
                  title="Listado por proveedor asignado (requiere asignar 3 proveedores primero)"
                >
                  <FileText className="h-3 w-3" />
                  PDF por proveedor
                </button>
              </>
            )}
            <button
              onClick={() => setStateFilter("")}
              className="text-xs px-3 py-2 border border-slate-300 rounded-md bg-white hover:bg-slate-50"
            >
              Quitar filtro
            </button>
          </div>
        </div>
      )}

      {showBulk && stateFilter === "solo_marca" && (
        <BulkPrefsWizard molecules={sorted} reload={reload} onDone={() => setShowBulk(false)} />
      )}
      {showCoverage && (
        <UnassignedCoverageModal dispId={id} reload={reload} onClose={() => setShowCoverage(false)} />
      )}
      {showBestLabs && stateFilter === "solo_marca" && (
        <BestLabsSuggestModal dispId={id} onClose={() => setShowBestLabs(false)} reload={reload} />
      )}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <input
            data-testid="molecule-filter"
            type="text"
            placeholder="Buscar molécula (G.H.)…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 min-w-[200px] px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
          <span className="text-xs text-slate-500 italic">Pulsa en cualquier cabecera de la tabla para ordenar.</span>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mr-1">Filtrar estado:</span>
          <button onClick={() => setStateFilter("")} className={`text-xs px-3 py-1 rounded-full border ${stateFilter === "" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            Todos ({data.molecules.length})
          </button>
          {Object.keys(STATE).filter((k) => k !== "no_data").map((k) => {
            const cnt = data.molecules.filter((m) => m.state_key === k).length;
            if (!cnt) return null;
            const st = STATE[k];
            return (
              <button
                key={k}
                onClick={() => setStateFilter(stateFilter === k ? "" : k)}
                data-testid={`mol-filter-${k}`}
                className={`text-xs px-3 py-1 rounded-full border inline-flex items-center gap-1.5 ${stateFilter === k ? `${st.bg} ${st.text} border-current font-semibold` : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
                {st.label} ({cnt})
              </button>
            );
          })}
          {autoAssignedCount > 0 && (
            <button
              onClick={() => setAutoOnly(!autoOnly)}
              data-testid="filter-auto-only"
              className={`text-xs px-3 py-1 rounded-full border inline-flex items-center gap-1.5 ${
                autoOnly
                  ? "bg-violet-100 text-violet-800 border-violet-400 font-semibold"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
              title="Filtrar sólo moléculas con proveedor asignado automáticamente por el sistema (mayor stock entre los proveedores con condición)."
            >
              <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
              Sólo AUTO ({autoAssignedCount})
            </button>
          )}
          {labFilter && (
            <button
              onClick={() => setLabFilter("")}
              data-testid="filter-clear-lab"
              className="text-xs px-3 py-1 rounded-full border inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 border-blue-300 font-semibold hover:bg-blue-200"
              title={`Filtrando por proveedor "${labFilter}". Click para quitar.`}
            >
              Proveedor: {labFilter.length > 28 ? labFilter.slice(0, 28) + "…" : labFilter}
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      <div className="text-xs text-slate-500 mb-3 font-mono-num">
        {sorted.length} moléculas {stateFilter ? `· filtro: ${STATE[stateFilter]?.label}` : ""}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="molecules-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-[0.15em] text-slate-500">
            <tr>
              <th className="p-3 w-8"></th>
              <SortableTh k="gh" sortKey={sortKey} direction={direction} toggle={toggle}>Molécula (G.H.)</SortableTh>
              <SortableTh k="pvl_conj" sortKey={sortKey} direction={direction} toggle={toggle} align="right">PVL conj.</SortableTh>
              <SortableTh k="marca_units" sortKey={sortKey} direction={direction} toggle={toggle} align="right" className="bg-slate-100">Marca u.</SortableTh>
              <SortableTh k="marca_euros" sortKey={sortKey} direction={direction} toggle={toggle} align="right" className="bg-slate-100">Marca €</SortableTh>
              <SortableTh k="generico_units" sortKey={sortKey} direction={direction} toggle={toggle} align="right" className="bg-blue-50">Gen. u.</SortableTh>
              <SortableTh k="generico_euros" sortKey={sortKey} direction={direction} toggle={toggle} align="right" className="bg-blue-50">Gen. €</SortableTh>
              <SortableTh k="generic_share_pct" sortKey={sortKey} direction={direction} toggle={toggle} align="right">% camb.</SortableTh>
              <SortableTh k="state_key" sortKey={sortKey} direction={direction} toggle={toggle}>Estado</SortableTh>
              <SortableTh k="preferred_lab" sortKey={sortKey} direction={direction} toggle={toggle}>Compra preferida</SortableTh>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {groups ? (
              groups.map((g, gi) => (
                <FragmentRow key={`grp-${gi}-${g.root}`}>
                  {g.isGroup && (
                    <tr className="bg-violet-50/70" data-testid={`molgroup-${g.root}`}>
                      <td colSpan={10} className="px-3 py-2 border-l-4 border-violet-400">
                        <div className="flex items-center justify-between gap-3 flex-wrap text-xs">
                          <div className="font-bold text-violet-900 uppercase tracking-wider flex items-center gap-2">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500" />
                            Grupo · {g.root}
                            <span className="text-violet-700/70 font-normal normal-case tracking-normal">
                              ({g.items.length} presentaciones)
                            </span>
                          </div>
                          {g.medianPct != null && (
                            <div className="text-violet-800">
                              Mediana % gen. del grupo: <strong className="font-mono-num">{g.medianPct.toFixed(1)}%</strong>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  {g.items.map((m) => renderMolRow(m, g.isGroup))}
                </FragmentRow>
              ))
            ) : (
              sorted.map((m) => renderMolRow(m, false))
            )}
          </tbody>
        </table>
        {sorted.length === 0 && <div className="p-8 text-center text-slate-500">Sin moléculas para este filtro.</div>}
      </div>
      {empModalGh && (
        <MoleculeEmployeesModal
          dispensationId={id}
          gh={empModalGh}
          onClose={() => setEmpModalGh(null)}
        />
      )}
    </div>
  );
}

/* ---------- Top 25 ---------- */

function TopTab({ id, data, molMap, stockMeta }) {
  const sum25 = data.top_25_summary || {};
  const [presentationOpen, setPresentationOpen] = useState(false);
  const [bestOptionOpen, setBestOptionOpen] = useState(false);
  return (
    <div data-testid="top-tab" className="space-y-6">
      <Top25ShareCard
        marcaEuros={sum25.marca_euros || 0}
        marcaUnits={sum25.marca_units || 0}
        pct={sum25.pct_over_total_marca || 0}
        label={data.label}
        onOpenPresentation={() => setPresentationOpen(true)}
      />

      <button
        onClick={() => setBestOptionOpen(true)}
        className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-3 rounded-xl shadow-sm transition-colors"
        data-testid="top25-best-option-btn"
      >
        <Sparkles className="h-4 w-4" />
        ¿Quieres ver la opción más rentable del Top 25 entre tus laboratorios?
      </button>

      {bestOptionOpen && (
        <Top25BestOptionModal
          dispensationId={id}
          label={data.label}
          onClose={() => setBestOptionOpen(false)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <TopList
            title="25 moléculas con más € en marca"
            subtitle="Solo importe de marca · sin genéricos"
            items={data.top_25_euros}
            valueKey="marca_euros"
            valueFmt={fmtEur}
            molMap={molMap}
            stockMeta={stockMeta}
            dispensationId={id}
            exportSection="top_euros"
          />
        </div>
        <div>
          <TopList
            title="25 moléculas con más unidades de marca"
            subtitle="Solo unidades de marca · sin genéricos"
            items={data.top_25_units}
            valueKey="marca_units"
            valueFmt={fmtNum}
            molMap={molMap}
            stockMeta={stockMeta}
            dispensationId={id}
            exportSection="top_units"
          />
        </div>
      </div>

      {/* Tercer ranking: TOTAL unidades dispensadas (marca + genérico) */}
      {Array.isArray(data.top_25_total_units) && data.top_25_total_units.length > 0 && (
        <div>
          <TopList
            title="25 moléculas con más dispensaciones (unidades totales)"
            subtitle="Suma de marca + genérico — las moléculas que más mueve tu farmacia"
            items={data.top_25_total_units}
            valueKey="total_units"
            valueFmt={fmtNum}
            molMap={molMap}
            stockMeta={stockMeta}
            dispensationId={id}
            exportSection="top_units"
          />
        </div>
      )}

      {presentationOpen && (
        <EmployeePresentationMode
          label={data.label}
          topEuros={data.top_25_euros}
          topUnits={data.top_25_units}
          onClose={() => setPresentationOpen(false)}
        />
      )}
    </div>
  );
}

/* ---------- Modo empleado (presentación fullscreen sin importes) ---------- */

function EmployeePresentationMode({ label, topEuros, topUnits, onClose }) {
  const [tab, setTab] = useState("euros"); // "euros" | "units"
  const [indications, setIndications] = useState({});
  const [indLoading, setIndLoading] = useState(false);
  const [familyFilter, setFamilyFilter] = useState("");

  // Cerrar con Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setTab("units");
      if (e.key === "ArrowLeft") setTab("euros");
    };
    window.addEventListener("keydown", onKey);
    // Bloquea scroll del body mientras esté abierto
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Cargar indicaciones una vez, para todas las moléculas de ambas listas
  useEffect(() => {
    const allGhs = Array.from(
      new Set([...(topEuros || []), ...(topUnits || [])].map((m) => m.gh).filter(Boolean)),
    );
    if (!allGhs.length) return;
    setIndLoading(true);
    api
      .post("/molecules/indications", { ghs: allGhs })
      .then((r) => setIndications(r.data?.indications || {}))
      .catch(() => { /* silent */ })
      .finally(() => setIndLoading(false));
  }, [topEuros, topUnits]);

  const items = tab === "euros" ? (topEuros || []) : (topUnits || []);

  // Lista de familias disponibles (para el filtro)
  const families = useMemo(() => {
    const s = new Set();
    items.forEach((m) => {
      const ind = indications[m.gh];
      if (ind?.family) s.add(ind.family);
    });
    return ["", ...Array.from(s).sort((a, b) => a.localeCompare(b, "es"))];
  }, [items, indications]);

  const filtered = useMemo(() => {
    if (!familyFilter) return items;
    return items.filter((m) => (indications[m.gh]?.family || "") === familyFilter);
  }, [items, indications, familyFilter]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950 text-white flex flex-col animate-in fade-in duration-200"
      data-testid="employee-presentation-mode"
      role="dialog"
      aria-modal="true"
    >
      {/* Cabecera */}
      <div className="flex items-center justify-between gap-4 px-8 py-5 border-b border-white/10 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 print:hidden">
        <div className="flex items-center gap-4">
          <div className="h-11 w-11 rounded-lg bg-violet-600 grid place-items-center shadow-lg shadow-violet-500/20">
            <Presentation className="h-5 w-5" strokeWidth={2} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-violet-300/80 font-bold">
              Modo empleado · Reunión de mostrador
            </div>
            <div className="font-display text-2xl font-bold leading-tight tracking-tight">
              Top 25 moléculas — {label || "Estudio"}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Selector de vista */}
          <div className="inline-flex items-center rounded-lg bg-white/10 border border-white/10 p-1 text-sm font-semibold" data-testid="presentation-tab-switcher">
            <button
              type="button"
              onClick={() => setTab("euros")}
              className={`px-4 py-1.5 rounded-md transition-colors ${tab === "euros" ? "bg-white text-slate-950 shadow" : "text-slate-300 hover:text-white"}`}
              data-testid="presentation-tab-euros"
            >
              Top 25 marca
            </button>
            <button
              type="button"
              onClick={() => setTab("units")}
              className={`px-4 py-1.5 rounded-md transition-colors ${tab === "units" ? "bg-white text-slate-950 shadow" : "text-slate-300 hover:text-white"}`}
              data-testid="presentation-tab-units"
            >
              Top 25 unidades
            </button>
          </div>

          {/* Filtro por familia */}
          {families.length > 2 && (
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs">
              <Filter className="h-3.5 w-3.5 text-violet-300" />
              <select
                value={familyFilter}
                onChange={(e) => setFamilyFilter(e.target.value)}
                className="bg-transparent text-white outline-none pr-2 text-xs font-semibold [&>option]:text-slate-900"
                data-testid="presentation-family-filter"
              >
                {families.map((f) => (
                  <option key={f} value={f}>{f || "Todas las familias"}</option>
                ))}
              </select>
            </div>
          )}

          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-200 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-md transition-colors"
            title="Imprimir esta vista"
            data-testid="presentation-print-btn"
          >
            <Printer className="h-3.5 w-3.5" /> Imprimir
          </button>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/10 px-3 py-2 rounded-md transition-colors"
            data-testid="presentation-close-btn"
            title="Cerrar (Esc)"
          >
            <X className="h-3.5 w-3.5" /> Salir
          </button>
        </div>
      </div>

      {/* Body: grid de tarjetas */}
      <div className="flex-1 overflow-y-auto px-8 py-6 print:px-4 print:py-2">
        {indLoading && (
          <div className="text-center text-slate-400 text-sm mb-4" data-testid="presentation-loading">
            Cargando indicaciones terapéuticas…
          </div>
        )}
        {filtered.length === 0 ? (
          <div className="text-center text-slate-400 py-24">
            <div className="text-lg mb-1">Sin moléculas con este filtro</div>
            <button
              type="button"
              onClick={() => setFamilyFilter("")}
              className="text-violet-300 hover:text-violet-200 text-sm underline"
            >
              Quitar filtro
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 print:grid-cols-2 print:gap-2">
            {filtered.map((m, idx) => {
              const rank = items.indexOf(m) + 1;
              const ind = indications[m.gh];
              return (
                <div
                  key={m.gh + idx}
                  className="rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] p-5 transition-colors print:bg-white print:text-slate-900 print:border-slate-300"
                  data-testid={`presentation-card-${rank}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="font-display text-4xl font-bold text-violet-300/90 leading-none tabular-nums w-12 print:text-slate-500">
                      {String(rank).padStart(2, "0")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg font-bold leading-snug tracking-tight print:text-slate-950">
                        {m.gh}
                      </div>
                      {ind ? (
                        <>
                          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-violet-500/15 border border-violet-400/25 px-2.5 py-0.5 text-[11px] font-bold text-violet-200 uppercase tracking-wider print:bg-violet-50 print:text-violet-800 print:border-violet-200">
                            {ind.family}
                          </div>
                          <div className="mt-2 text-sm font-semibold text-white print:text-slate-800">
                            {ind.short}
                          </div>
                          <div className="mt-1 text-xs text-slate-300 leading-relaxed print:text-slate-600">
                            {ind.full}
                          </div>
                        </>
                      ) : (
                        <div className="mt-2 text-xs text-slate-500 italic">
                          Sin indicación curada · consulta ficha técnica AEMPS
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer: pistas de teclado */}
      <div className="px-8 py-3 border-t border-white/10 bg-black/40 flex items-center justify-between text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 print:hidden">
        <span>
          <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-slate-300 font-mono">Esc</kbd> cerrar ·{" "}
          <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-slate-300 font-mono">←</kbd>{" "}
          <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-slate-300 font-mono">→</kbd> cambiar de lista
        </span>
        <span>{filtered.length} / {items.length} moléculas</span>
      </div>
    </div>
  );
}

function Top25ShareCard({ marcaEuros, marcaUnits, pct, label, onOpenPresentation }) {
  const cardRef = useRef(null);
  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        backgroundColor: "#0F172A",
        cacheBust: true,
      });
      const a = document.createElement("a");
      a.download = `farmaclass-top25-${(label || "estudio").toLowerCase().replace(/\s+/g, "-")}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("Imagen descargada. ¡Compártela en WhatsApp!");
    } catch (e) {
      toast.error("No se pudo generar la imagen");
    }
  };
  const waMsg = encodeURIComponent(
    `Top 25 moléculas en marca de mi estudio (${label || ""}): ${fmtEur(marcaEuros)} (${pct.toFixed(2)}% del total marca). Detectado con FarmaClass → https://farmaclass.es`,
  );

  return (
    <div className="space-y-2" data-testid="top-25-share-block">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Resumen Top 25 · Marca</div>
        <div className="flex items-center gap-2">
          {onOpenPresentation && (
            <button
              onClick={onOpenPresentation}
              data-testid="top25-presentation-btn"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-violet-600 hover:bg-violet-700 px-3 py-1.5 rounded-md transition-colors shadow-sm"
              title="Abre el Top 25 a pantalla completa, sin importes — ideal para enseñárselo al empleado"
            >
              <Presentation className="h-3 w-3" /> Modo empleado
            </button>
          )}
          <button
            onClick={handleDownload}
            data-testid="top25-download-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 bg-white border border-slate-200 px-3 py-1.5 rounded-md hover:border-slate-400 transition-colors"
          >
            <Download className="h-3 w-3" /> Descargar imagen
          </button>
          <a
            href={`https://wa.me/?text=${waMsg}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="top25-whatsapp-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-slate-950 hover:bg-slate-800 px-3 py-1.5 rounded-md transition-colors"
          >
            <Share2 className="h-3 w-3" /> WhatsApp
          </a>
        </div>
      </div>

      <div
        ref={cardRef}
        className="relative overflow-hidden rounded-2xl text-white"
        style={{
          background: "linear-gradient(135deg, #0F172A 0%, #1E293B 60%, #0F172A 100%)",
        }}
      >
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 70%, #fff 1px, transparent 1px)",
          backgroundSize: "40px 40px, 60px 60px",
        }} />
        <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full" style={{ background: "radial-gradient(circle, rgba(244,63,94,0.18) 0%, transparent 65%)" }} />

        <div className="relative p-7 lg:p-9 grid grid-cols-1 lg:grid-cols-2 gap-7">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-rose-300/90 font-bold mb-3">
              <Trophy className="h-3 w-3" />
              FarmaClass · Top 25 marca
            </div>
            <h3 className="font-display text-2xl lg:text-3xl font-bold leading-tight mb-1">
              Pérdida estimada del mes
            </h3>
            <div className="text-slate-400 text-sm mb-5">
              25 moléculas con más marca · {fmtNum(marcaUnits)} uds · {label}
            </div>
            <div className="flex items-baseline gap-3">
              <div className="font-display text-6xl font-bold font-mono-num text-rose-400">
                {fmtEur(marcaEuros)}
              </div>
            </div>
            <div className="text-xs text-slate-400 mt-2 max-w-md">
              Si convertimos estas 25 moléculas a genérico, recuperaríamos casi todo este importe.
            </div>
          </div>

          <div className="flex flex-col justify-center lg:border-l lg:border-white/10 lg:pl-7">
            <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-bold mb-1">Impacto sobre el total marca</div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-7xl font-bold font-mono-num text-amber-300">
                {pct.toFixed(1)}<span className="text-4xl text-amber-300/70">%</span>
              </span>
            </div>
            <div className="text-slate-300 text-sm mt-2">
              25 moléculas concentran el <span className="font-bold text-white">{pct.toFixed(1)}%</span> del coste total de marca.
            </div>
          </div>
        </div>

        <div className="relative px-7 lg:px-9 py-3 bg-white/[0.03] border-t border-white/5 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
          <span>farmaclass.es · análisis EFG</span>
          <span>{new Date().toLocaleDateString("es-ES", { month: "long", year: "numeric" })}</span>
        </div>
      </div>
    </div>
  );
}

function TopList({ title, subtitle, items, valueKey, valueFmt, molMap, stockMeta, dispensationId, exportSection }) {
  const max = Math.max(...items.map((i) => i[valueKey] || 0), 1);
  const [open, setOpen] = useState({});
  const [empModalGh, setEmpModalGh] = useState(null);
  const [viewMode, setViewMode] = useState("financial"); // "financial" | "therapeutic"
  const [indications, setIndications] = useState({});
  const [indLoading, setIndLoading] = useState(false);
  // Iter-98 · Ranking "Vs Mercado"
  const [bench, setBench] = useState(null); // {province_name, items:{gh:{national,province}}}
  const [benchScope, setBenchScope] = useState("national"); // "national" | "province"
  const [sortMode, setSortMode] = useState("volume"); // "market" | "volume" (por defecto: volumen)

  useEffect(() => {
    if (!items?.length) return;
    let cancelled = false;
    api
      .post("/benchmark/molecules", { ghs: items.map((m) => m.gh).filter(Boolean) })
      .then((r) => { if (!cancelled) setBench(r.data); })
      .catch(() => { if (!cancelled) setBench({ items: {} }); });
    return () => { cancelled = true; };
  }, [items]);

  const marketPct = (gh) => {
    const slot = bench?.items?.[gh];
    if (!slot) return null;
    const pick = benchScope === "province" ? slot.province : benchScope === "ccaa" ? slot.ccaa : slot.national;
    return pick ? pick.pct_generic : null;
  };
  const marketDiff = (m) => {
    const mkt = marketPct(m.gh);
    if (mkt == null) return null;
    return Number(m.generic_share_pct || 0) - mkt;
  };
  const hasProvinceData = !!(bench && Object.values(bench.items || {}).some((s) => s?.province));
  const hasCcaaData = !!(bench && Object.values(bench.items || {}).some((s) => s?.ccaa));
  const ordered = useMemo(() => {
    const arr = [...(items || [])];
    if (sortMode !== "market" || !bench) return arr;
    return arr.sort((a, b) => {
      const da = marketDiff(a);
      const dbv = marketDiff(b);
      if (da == null && dbv == null) return (b[valueKey] || 0) - (a[valueKey] || 0);
      if (da == null) return 1;
      if (dbv == null) return -1;
      return da - dbv; // peores primero (más por debajo del mercado)
    });
  }, [items, bench, benchScope, sortMode, valueKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (viewMode !== "therapeutic" || !items?.length) return;
    if (Object.keys(indications).length > 0) return;
    setIndLoading(true);
    api
      .post("/molecules/indications", { ghs: items.map((m) => m.gh) })
      .then((r) => setIndications(r.data?.indications || {}))
      .catch(() => {})
      .finally(() => setIndLoading(false));
  }, [viewMode, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = (gh, el) => {
    const wasOpen = !!open[gh];
    setOpen({ ...open, [gh]: !wasOpen });
    if (!wasOpen) {
      // Hacer scroll al panel cuando se abre
      setTimeout(() => {
        try { el?.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch {}
      }, 50);
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="p-5 border-b border-slate-200 flex items-center gap-3">
        <div className="h-9 w-9 rounded-md bg-slate-950 text-white grid place-items-center">
          <Trophy className="h-4 w-4" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
        <div className="ml-auto inline-flex items-center gap-2">
          {exportSection && (
            <div className="inline-flex items-center gap-1.5" data-testid={`top-list-export-${exportSection}`}>
              <button
                type="button"
                onClick={() => downloadXlsx(`/dispensations/${dispensationId}/export-section?section=${exportSection}&format=xlsx&view=${viewMode === "therapeutic" ? "indication" : "financial"}`, `${exportSection}_${viewMode}.xlsx`)}
                data-testid={`export-${exportSection}-xlsx`}
                className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 border border-slate-200 rounded-md bg-white hover:bg-slate-50 text-slate-700"
                title={viewMode === "therapeutic" ? "Descargar Excel con familia + indicación" : "Descargar Excel con importes y unidades"}
              >
                <Download className="h-3 w-3" /> Excel
              </button>
              <button
                type="button"
                onClick={() => downloadXlsx(`/dispensations/${dispensationId}/export-section?section=${exportSection}&format=pdf&view=${viewMode === "therapeutic" ? "indication" : "financial"}`, `${exportSection}_${viewMode}.pdf`)}
                data-testid={`export-${exportSection}-pdf`}
                className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 border border-slate-200 rounded-md bg-white hover:bg-slate-50 text-slate-700"
                title={viewMode === "therapeutic" ? "Descargar PDF con familia + indicación" : "Descargar PDF con importes y unidades"}
              >
                <FileText className="h-3 w-3" /> PDF
              </button>
            </div>
          )}
          <div className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 p-0.5 text-[11px] font-semibold" data-testid="top-list-view-toggle">
            <button
              type="button"
              onClick={() => setViewMode("financial")}
              className={`px-2.5 py-1 rounded ${viewMode === "financial" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
              data-testid="view-mode-financial"
            >
              € / uds
            </button>
            <button
              type="button"
              onClick={() => setViewMode("therapeutic")}
              className={`px-2.5 py-1 rounded ${viewMode === "therapeutic" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
              data-testid="view-mode-therapeutic"
              title="Ver la indicación terapéutica de cada molécula"
            >
              Indicación
            </button>
          </div>
        </div>
      </div>
      <div className="px-5 py-2.5 border-b border-slate-200 bg-slate-50/60 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="top-list-market-bar">
        <div className="inline-flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500">Orden</span>
          <div className="inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5 text-[11px] font-semibold">
            <button
              type="button"
              onClick={() => setSortMode("market")}
              className={`px-2.5 py-1 rounded ${sortMode === "market" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
              data-testid="sort-mode-market"
              title="Ordenar por la diferencia con la media de mercado: primero donde más margen de mejora tienes"
            >
              Vs Mercado
            </button>
            <button
              type="button"
              onClick={() => setSortMode("volume")}
              className={`px-2.5 py-1 rounded ${sortMode === "volume" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
              data-testid="sort-mode-volume"
              title="Ordenar por volumen (el orden original del Top 25)"
            >
              Volumen
            </button>
          </div>
        </div>
        <div className="inline-flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500">Comparar con</span>
          <div className="inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5 text-[11px] font-semibold">
            <button
              type="button"
              onClick={() => setBenchScope("national")}
              className={`px-2.5 py-1 rounded ${benchScope === "national" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
              data-testid="bench-scope-national"
            >
              Nacional
            </button>
            <button
              type="button"
              onClick={() => setBenchScope("ccaa")}
              disabled={!hasCcaaData}
              className={`px-2.5 py-1 rounded ${benchScope === "ccaa" ? "bg-slate-900 text-white" : hasCcaaData ? "text-slate-500 hover:text-slate-900" : "text-slate-300 cursor-not-allowed"}`}
              data-testid="bench-scope-ccaa"
              title={hasCcaaData ? "Comparar con la media de tu comunidad autónoma" : "Aún no hay datos suficientes de tu comunidad autónoma"}
            >
              {bench?.ccaa_name || "Comunidad"}
            </button>
            <button
              type="button"
              onClick={() => setBenchScope("province")}
              disabled={!hasProvinceData}
              className={`px-2.5 py-1 rounded ${benchScope === "province" ? "bg-slate-900 text-white" : hasProvinceData ? "text-slate-500 hover:text-slate-900" : "text-slate-300 cursor-not-allowed"}`}
              data-testid="bench-scope-province"
              title={hasProvinceData ? "Comparar con la media de tu provincia" : "Aún no hay datos suficientes de tu provincia"}
            >
              {bench?.province_name || "Provincia"}
            </button>
          </div>
        </div>
        <span className="text-[11px] text-slate-500 ml-auto">
          {!bench ? "Cargando medias de mercado…" : "pp = puntos porcentuales de % de cambio a genérico frente a la media"}
        </span>
      </div>
      <ol className="divide-y divide-slate-200">
        {ordered.map((m, i) => {
          const w = ((m[valueKey] || 0) / max) * 100;
          const isOpen = !!open[m.gh];
          const fullMol = molMap[m.gh] || m;
          return (
            <li key={m.gh} className={isOpen ? "bg-amber-50/30 ring-1 ring-amber-200" : ""}>
              <button
                onClick={(e) => handleToggle(m.gh, e.currentTarget.parentElement)}
                className="w-full text-left p-3 hover:bg-slate-50"
                type="button"
                data-testid={`top-row-${m.gh}`}
              >
                <div className="flex items-start gap-3 mb-1.5">
                  <span className="font-mono-num text-xs text-slate-400 w-5 text-right pt-0.5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      <span className="truncate">{m.gh}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEmpModalGh(m.gh); }}
                        data-testid={`btn-top-employees-${m.gh}`}
                        title="Ver oportunidades y % de cambio por empleado en esta molécula"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-slate-900 text-white hover:bg-slate-700 transition-colors flex-shrink-0"
                      >
                        <Users className="h-3 w-3" />
                        Empleados
                      </button>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 font-mono-num">
                      {viewMode === "therapeutic" ? (
                        (() => {
                          const ind = indications[m.gh];
                          if (indLoading) return <span className="text-slate-400 italic">Cargando indicación…</span>;
                          if (!ind) return <span className="text-slate-500 italic">Sin indicación curada · consulta ficha técnica AEMPS</span>;
                          return (
                            <span>
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-100 border border-sky-200 text-sky-900 text-[10px] font-bold uppercase tracking-wider mr-1.5 font-sans">
                                {ind.family}
                              </span>
                              <span className="font-sans font-semibold text-slate-900">{ind.short}</span>
                              <span className="text-slate-500 font-sans"> · {ind.full}</span>
                            </span>
                          );
                        })()
                      ) : (
                        <>
                          <span className="font-semibold text-slate-900">{fmtNum(m.marca_units)}</span> u marca ·{" "}
                          <span className="font-semibold text-slate-900">{fmtEur(m.marca_euros)}</span>
                          {" · "}
                          <span className="font-semibold text-blue-700">{fmtNum(m.generico_units)}</span> u gen ·{" "}
                          <span className="font-semibold text-blue-700">{fmtEur(m.generico_euros)}</span>
                          {m.gh_pvl != null && <span className="text-slate-500"> · PVL conj. {fmtEur(m.gh_pvl)}</span>}
                        </>
                      )}
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <StateBadge stateKey={m.state_key} pct={m.generic_share_pct} />
                      {(() => {
                        const mkt = marketPct(m.gh);
                        if (mkt == null) {
                          return (
                            <span className="text-[10px] text-slate-400 font-mono-num" data-testid={`vs-market-none-${m.gh}`}>
                              sin media de mercado
                            </span>
                          );
                        }
                        const diff = Number(m.generic_share_pct || 0) - mkt;
                        const good = diff >= 0;
                        return (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold font-mono-num border ${
                              good
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-rose-50 text-rose-700 border-rose-200"
                            }`}
                            data-testid={`vs-market-${m.gh}`}
                            title={`Tu farmacia ${Number(m.generic_share_pct || 0).toFixed(1)}% · media ${benchScope === "province" ? bench?.province_name || "provincial" : benchScope === "ccaa" ? bench?.ccaa_name || "autonómica" : "nacional"} ${mkt.toFixed(1)}%`}
                          >
                            {good ? "+" : ""}{diff.toFixed(1)} pp vs {benchScope === "province" ? "prov." : benchScope === "ccaa" ? "com." : "nac."} ({mkt.toFixed(1)}%)
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="font-mono-num text-sm font-semibold whitespace-nowrap pt-0.5">
                    {valueFmt(m[valueKey])}
                  </div>
                  {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                </div>
                <div className="ml-8 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-slate-900" style={{ width: `${w}%` }} />
                </div>
              </button>
              {isOpen && <GhDetail molecule={fullMol} stockMeta={stockMeta} />}
            </li>
          );
        })}
      </ol>
      {empModalGh && dispensationId && (
        <MoleculeEmployeesModal
          dispensationId={dispensationId}
          gh={empModalGh}
          onClose={() => setEmpModalGh(null)}
        />
      )}
    </div>
  );
}

/* ---------- Suppliers ---------- */

function SuppliersTab({ id, data, labs, labsCatalog, reload, onFocusMolecules, initialOpenSup = null }) {
  const [openSup, setOpenSup] = useState(initialOpenSup);
  const [mode, setMode] = useState("view"); // view | transfer | redistribute
  const [agreements, setAgreements] = useState({}); // {laboratorio: true}
  const { user } = useAuth();
  // Marca blanca: sin módulo de condiciones, la condición se marca aquí mismo
  const isSandoz = user?.brand === "sandoz";

  // Si nos llega ?lab=X y ese lab existe en suppliers, hacemos scroll para destacarlo.
  useEffect(() => {
    if (!initialOpenSup) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="supplier-row-${initialOpenSup}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, [initialOpenSup]);

  const loadAgreements = async () => {
    try {
      const m = {};
      if (isSandoz) {
        const r = await api.get("/sandoz/labs");
        (r.data?.marked || []).forEach((lab) => { m[lab] = true; });
      } else {
        const r = await api.get("/supplier-agreements");
        (r.data?.items || []).forEach((x) => { if (x.has_agreement) m[x.laboratorio] = true; });
      }
      setAgreements(m);
    } catch {
      // ignore
    }
  };

  useEffect(() => { loadAgreements(); }, [isSandoz]);

  const toggleAgreement = async (lab, e) => {
    e?.stopPropagation();
    const newVal = !agreements[lab];
    try {
      if (isSandoz) {
        const next = Object.keys(agreements).filter((k) => agreements[k] && k !== lab);
        if (newVal) next.push(lab);
        await api.put("/sandoz/labs", { labs: next });
      } else {
        await api.post("/supplier-agreements", { laboratorio: lab, has_agreement: newVal });
      }
      setAgreements((prev) => ({ ...prev, [lab]: newVal }));
      toast.success(newVal ? `${lab} marcado con condición de compra` : `${lab} sin condición de compra`);
    } catch (err) {
      toast.error("No se pudo actualizar");
    }
  };

  const totalE = data.suppliers.reduce((s, x) => s + x.euros, 0);

  // Map: laboratorio → { assigned: nº moléculas con preferred_lab = este lab, auto: nº de ellas marcadas AUTO }
  const assignmentsByLab = useMemo(() => {
    const m = {};
    (data.molecules || []).forEach((mol) => {
      const lab = mol.preferred_lab;
      if (!lab) return;
      const e = m[lab] || (m[lab] = { assigned: 0, auto: 0 });
      e.assigned += 1;
      if (mol.preferred_lab_auto) e.auto += 1;
    });
    return m;
  }, [data.molecules]);

  // Build map: lab -> Set<gh>  (only generic EFG)
  const labGhMap = useMemo(() => {
    const m = {};
    labsCatalog.forEach((c) => {
      if (!c.is_efg || c.type !== "generico" || !c.gh || !c.laboratorio) return;
      if (!m[c.laboratorio]) m[c.laboratorio] = new Set();
      m[c.laboratorio].add(c.gh);
    });
    return m;
  }, [labsCatalog]);

  // Build map: gh -> [{cn, name, laboratorio, pvl}]  (todos los CN que comparten ese GH)
  const ghLabsMap = useMemo(() => {
    const m = {};
    labsCatalog.forEach((c) => {
      if (!c.gh || !c.laboratorio) return;
      if (!m[c.gh]) m[c.gh] = [];
      m[c.gh].push({
        cn: c.cn,
        name: c.name,
        laboratorio: c.laboratorio,
        pvl: c.pvl,
        is_efg: c.is_efg,
        type: c.type,
      });
    });
    return m;
  }, [labsCatalog]);

  if (!data.suppliers.length) return <Empty msg="Sin proveedores identificados." />;

  const open = (lab) => {
    if (openSup === lab) {
      setOpenSup(null);
      setMode("view");
    } else {
      setOpenSup(lab);
      setMode("view");
    }
  };

  return (
    <div className="space-y-3" data-testid="suppliers-tab">
      <SectionExport id={id} section="suppliers" />
      {data.suppliers.map((sup) => {
        const pctE = totalE ? Math.round((sup.euros / totalE) * 100) : 0;
        const isOpen = openSup === sup.laboratorio;
        return (
          <div key={sup.laboratorio} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <button
              onClick={() => open(sup.laboratorio)}
              data-testid={`supplier-row-${sup.laboratorio}`}
              className="w-full p-5 hover:bg-slate-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  <div className="h-9 w-9 rounded-md bg-slate-100 grid place-items-center text-slate-700">
                    <Building2 className="h-4 w-4" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-2 flex-wrap">
                      {sup.laboratorio}
                      {agreements[sup.laboratorio] && (
                        <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                          ✓ Condición
                        </span>
                      )}
                      {assignmentsByLab[sup.laboratorio]?.assigned > 0 && (
                        <span
                          className="text-[10px] uppercase tracking-wider font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                          title={`Tienes asignadas ${assignmentsByLab[sup.laboratorio].assigned} mol. a este proveedor${assignmentsByLab[sup.laboratorio].auto ? ` (${assignmentsByLab[sup.laboratorio].auto} AUTO)` : ""}`}
                        >
                          {assignmentsByLab[sup.laboratorio].assigned} pref.
                          {assignmentsByLab[sup.laboratorio].auto > 0 && (
                            <span className="bg-violet-100 text-violet-800 px-1.5 py-0.5 rounded-full border border-violet-300 ml-1">
                              {assignmentsByLab[sup.laboratorio].auto} AUTO
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 font-mono-num">
                      {sup.lines} referencias · {fmtNum(sup.units)} u.
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {assignmentsByLab[sup.laboratorio]?.auto > 0 && onFocusMolecules && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); onFocusMolecules(sup.laboratorio, true); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onFocusMolecules(sup.laboratorio, true); } }}
                      data-testid={`list-auto-${sup.laboratorio}`}
                      title="Abrir tab Moléculas con sólo las AUTO de este proveedor"
                      className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 inline-flex items-center gap-1 cursor-pointer select-none"
                    >
                      Listar sus AUTO →
                    </span>
                  )}
                  <span
                    role={isSandoz ? "button" : undefined}
                    tabIndex={isSandoz ? 0 : undefined}
                    onClick={isSandoz ? (e) => toggleAgreement(sup.laboratorio, e) : (e) => e.stopPropagation()}
                    onKeyDown={isSandoz ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleAgreement(sup.laboratorio, e); } } : undefined}
                    data-testid={`agreement-toggle-${sup.laboratorio}`}
                    title={
                      isSandoz
                        ? (agreements[sup.laboratorio] ? "Quitar condición de compra" : "Marcar como proveedor con condición de compra")
                        : "La condición se marca en Condiciones de laboratorios"
                    }
                    className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border select-none ${isSandoz ? "transition-colors cursor-pointer" : "cursor-default"} ${agreements[sup.laboratorio] ? `bg-emerald-50 text-emerald-700 border-emerald-300${isSandoz ? " hover:bg-emerald-100" : ""}` : `bg-white text-slate-500 border-slate-300${isSandoz ? " hover:bg-slate-50" : ""}`}`}
                  >
                    {agreements[sup.laboratorio] ? "Con condición" : "Sin condición"}
                  </span>
                  <div className="text-right">
                    <div className="font-mono-num font-semibold text-lg">{fmtEur(sup.euros)}</div>
                    <div className="text-xs text-slate-500 font-mono-num">{pctE}%</div>
                  </div>
                </div>
              </div>
              <div className="mt-3 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                <div className="bg-slate-900 h-full" style={{ width: `${pctE}%` }} />
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-slate-200 p-5 bg-slate-50/40">
                {/* Mode tabs */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <ModeBtn active={mode === "view"} onClick={() => setMode("view")} label={`Detalle (${sup.products.length})`} />
                  <ModeBtn active={mode === "transfer"} onClick={() => setMode("transfer")} label="Traslado simple" icon={<ArrowRightLeft className="h-3 w-3" />} />
                  <ModeBtn active={mode === "redistribute"} onClick={() => setMode("redistribute")} label="Redistribuir entre 3 labs" icon={<ArrowRightLeft className="h-3 w-3" />} />
                </div>

                {mode === "view" && <SupplierProductsList products={sup.products} ghLabsMap={ghLabsMap} currentLab={sup.laboratorio} />}
                {mode === "transfer" && (
                  <SimpleTransfer products={sup.products} labs={labs} reload={reload} onDone={() => { setOpenSup(null); setMode("view"); }} />
                )}
                {mode === "redistribute" && (
                  <RedistributeWizard
                    products={sup.products}
                    labGhMap={labGhMap}
                    reload={reload}
                    onDone={() => { setOpenSup(null); setMode("view"); }}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ModeBtn({ active, onClick, label, icon }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-md border inline-flex items-center gap-1.5 ${
        active ? "bg-slate-950 text-white border-slate-950" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function SupplierProductsList({ products, ghLabsMap, currentLab }) {
  const sort = useTableSort(products || []);
  const [openCn, setOpenCn] = useState(null);
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-[0.15em] text-slate-500">
          <tr>
            <th className="p-2 text-left font-semibold w-6"></th>
            <SortTh field="cn" sort={sort} className="p-2 font-semibold">CN</SortTh>
            <SortTh field="name" sort={sort} className="p-2 font-semibold">Producto · G.H.</SortTh>
            <SortTh field="units" sort={sort} align="right" className="p-2 font-semibold">Unidades</SortTh>
            <SortTh field="euros" sort={sort} align="right" className="p-2 font-semibold">Euros</SortTh>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sort.sorted.map((p) => {
            const isOpen = openCn === p.cn;
            const alternatives = (p.gh && ghLabsMap?.[p.gh]) || [];
            const others = alternatives.filter((a) => a.laboratorio !== currentLab);
            const hasAlt = others.length > 0;
            return (
              <Fragment key={p.cn}>
                <tr
                  className={`${hasAlt ? "hover:bg-sky-50 cursor-pointer" : "hover:bg-slate-50"}`}
                  onClick={() => hasAlt && setOpenCn(isOpen ? null : p.cn)}
                  data-testid={`row-${p.cn}`}
                >
                  <td className="p-2 text-slate-400">
                    {hasAlt && (isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />)}
                  </td>
                  <td className="p-2 font-mono-num text-xs">{p.cn}</td>
                  <td className="p-2">
                    <div className="text-sm">{p.name}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-2">
                      <span>{p.gh || "Sin G.H."}</span>
                      {hasAlt && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-full">
                          {others.length} alternativa{others.length !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-2 text-right font-mono-num">{fmtNum(p.units)}</td>
                  <td className="p-2 text-right font-mono-num font-semibold">{fmtEur(p.euros)}</td>
                </tr>
                {isOpen && hasAlt && (
                  <tr className="bg-sky-50/40" data-testid={`alt-${p.cn}`}>
                    <td colSpan={5} className="p-3">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-sky-700 mb-2">
                        Otros proveedores con la misma molécula ({p.gh})
                      </div>
                      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                              <th className="p-2 text-left font-semibold">CN</th>
                              <th className="p-2 text-left font-semibold">Producto</th>
                              <th className="p-2 text-left font-semibold">Laboratorio</th>
                              <th className="p-2 text-left font-semibold">Tipo</th>
                              <th className="p-2 text-right font-semibold">PVL</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {others
                              .slice()
                              .sort((a, b) => (a.pvl || 0) - (b.pvl || 0))
                              .map((a, idx) => (
                                <tr key={`${a.cn}-${idx}`} className="hover:bg-slate-50">
                                  <td className="p-2 font-mono-num">{a.cn}</td>
                                  <td className="p-2">{a.name}</td>
                                  <td className="p-2 font-medium">{a.laboratorio}</td>
                                  <td className="p-2">
                                    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${
                                      a.is_efg && a.type === "generico"
                                        ? "bg-emerald-100 text-emerald-700"
                                        : a.is_efg && a.type === "marca"
                                          ? "bg-rose-100 text-rose-700"
                                          : "bg-slate-100 text-slate-700"
                                    }`}>
                                      {a.is_efg && a.type === "generico" ? "Genérico" : a.is_efg && a.type === "marca" ? "Marca" : "—"}
                                    </span>
                                  </td>
                                  <td className="p-2 text-right font-mono-num">{a.pvl ? `${Number(a.pvl).toFixed(2)} €` : "—"}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SimpleTransfer({ products, labs, reload, onDone }) {
  const [selected, setSelected] = useState({});
  const [toLab, setToLab] = useState("");
  const [busy, setBusy] = useState(null); // 'excel' | 'pdf'
  const toggle = (cn) => setSelected({ ...selected, [cn]: !selected[cn] });
  const allChecked = products.length > 0 && products.every((p) => selected[p.cn]);
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(Object.fromEntries(products.map((p) => [p.cn, true])));
  };
  const selectedCns = () => Object.keys(selected).filter((k) => selected[k]);

  const exportFile = async (fmt) => {
    const cns = selectedCns();
    if (!cns.length) return toast.error("Selecciona moléculas");
    if (!toLab.trim()) return toast.error("Indica el laboratorio destino");
    setBusy(fmt);
    try {
      const resp = await api.post(
        `/catalog/transfer-supplier/export?format=${fmt}`,
        { cns, to_laboratorio: toLab.trim() },
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], {
        type: fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `traslado-${toLab.trim().replace(/\s+/g, "_")}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Listado de ${cns.length} CN descargado`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al exportar");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="bg-sky-50 border border-sky-200 text-sky-900 text-xs rounded-md p-3 mb-3">
        <strong>Esta acción NO modifica el catálogo.</strong> Genera un listado descargable de los CN seleccionados para que se lo pidas al laboratorio destino o lo añadas a su pedido. La asignación de proveedores en tu farmacia no cambia.
      </div>
      <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md mb-3 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr>
              <th className="p-2"><input type="checkbox" checked={allChecked} onChange={toggleAll} className="h-4 w-4" /></th>
              <th className="p-2 text-left text-xs uppercase tracking-[0.15em] text-slate-500">CN</th>
              <th className="p-2 text-left text-xs uppercase tracking-[0.15em] text-slate-500">Producto</th>
              <th className="p-2 text-right text-xs uppercase tracking-[0.15em] text-slate-500">Unidades</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {products.map((p) => (
              <tr key={p.cn} className="hover:bg-slate-50">
                <td className="p-2"><input type="checkbox" checked={!!selected[p.cn]} onChange={() => toggle(p.cn)} className="h-4 w-4" /></td>
                <td className="p-2 font-mono-num text-xs">{p.cn}</td>
                <td className="p-2 text-sm">{p.name}</td>
                <td className="p-2 text-right font-mono-num text-xs">{fmtNum(p.units)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <input list="all-labs" placeholder="Laboratorio destino" value={toLab} onChange={(e) => setToLab(e.target.value)}
          className="flex-1 min-w-[220px] px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" />
        <datalist id="all-labs">{labs.map((l) => <option key={l} value={l} />)}</datalist>
        <button onClick={() => exportFile("excel")} data-testid="transfer-export-excel" disabled={busy === "excel"}
          className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md text-sm font-bold disabled:opacity-60">
          <Download className="h-3.5 w-3.5" />
          Descargar Excel ({selectedCns().length})
        </button>
        <button onClick={() => exportFile("pdf")} data-testid="transfer-export-pdf" disabled={busy === "pdf"}
          className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-md text-sm font-bold disabled:opacity-60">
          <Download className="h-3.5 w-3.5" />
          Descargar PDF
        </button>
      </div>
    </div>
  );
}

function RedistributeWizard({ products, labGhMap, reload, onDone }) {
  const [lab1, setLab1] = useState("");
  const [lab2, setLab2] = useState("");
  const [lab3, setLab3] = useState("");

  // Stage 1: candidates from full pool
  const allCns = useMemo(() => products.map((p) => p.cn), [products]);
  const ghByCn = useMemo(() => Object.fromEntries(products.map((p) => [p.cn, p.gh])), [products]);

  const cnsForLab = (cns, lab) => {
    if (!lab) return [];
    const ghs = labGhMap[lab];
    if (!ghs) return [];
    return cns.filter((cn) => ghByCn[cn] && ghs.has(ghByCn[cn]));
  };

  // Lab 1 candidates: any lab that has at least 1 of allCns
  const candidates1 = useMemo(() => {
    return Object.keys(labGhMap)
      .map((lab) => ({ lab, count: cnsForLab(allCns, lab).length }))
      .filter((x) => x.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [labGhMap, allCns]);

  const assigned1 = lab1 ? cnsForLab(allCns, lab1) : [];
  const remaining1 = allCns.filter((cn) => !assigned1.includes(cn));

  const candidates2 = useMemo(() => {
    if (!lab1) return [];
    return Object.keys(labGhMap)
      .filter((l) => l !== lab1)
      .map((lab) => ({ lab, count: cnsForLab(remaining1, lab).length }))
      .filter((x) => x.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [labGhMap, remaining1, lab1]);

  const assigned2 = lab2 ? cnsForLab(remaining1, lab2) : [];
  const remaining2 = remaining1.filter((cn) => !assigned2.includes(cn));

  const candidates3 = useMemo(() => {
    if (!lab1 || !lab2) return [];
    return Object.keys(labGhMap)
      .filter((l) => l !== lab1 && l !== lab2)
      .map((lab) => ({ lab, count: cnsForLab(remaining2, lab).length }))
      .filter((x) => x.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [labGhMap, remaining2, lab1, lab2]);

  const assigned3 = lab3 ? cnsForLab(remaining2, lab3) : [];
  const remaining3 = remaining2.filter((cn) => !assigned3.includes(cn));

  const [busy, setBusy] = useState(null);

  const exportFile = async (fmt) => {
    const groups = [
      { to_laboratorio: lab1, cns: assigned1 },
      { to_laboratorio: lab2, cns: assigned2 },
      { to_laboratorio: lab3, cns: assigned3 },
    ].filter((g) => g.to_laboratorio && g.cns.length > 0);
    if (!groups.length) return toast.error("Elige al menos un laboratorio destino con asignaciones");
    setBusy(fmt);
    try {
      const resp = await api.post(
        `/catalog/redistribute/export?format=${fmt}`,
        { assignments: groups },
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], {
        type: fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `redistribucion-${groups.length}labs.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Listado descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al exportar");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-700 leading-relaxed bg-sky-50 border border-sky-200 rounded-md p-3">
        <strong>Esta acción NO modifica el catálogo.</strong> Genera un listado descargable, agrupado por laboratorio destino, para que se lo envíes a cada proveedor o lo añadas a su pedido.
      </div>
      <div className="text-xs text-slate-600 leading-relaxed bg-blue-50 border border-blue-200 rounded-md p-3">
        <strong>Total a redistribuir:</strong> {products.length} CN. El primer laboratorio absorbe lo que tenga en su vademécum.
        El segundo coge los que no tenía el primero. El tercero coge los que no tenían ni el primero ni el segundo.
      </div>

      <RedistStage
        n={1}
        label="1er laboratorio"
        candidates={candidates1}
        value={lab1}
        onChange={(v) => { setLab1(v); setLab2(""); setLab3(""); }}
        assigned={assigned1.length}
        remaining={remaining1.length}
      />

      {lab1 && (
        <RedistStage
          n={2}
          label="2º laboratorio (para los restantes)"
          candidates={candidates2}
          value={lab2}
          onChange={(v) => { setLab2(v); setLab3(""); }}
          assigned={assigned2.length}
          remaining={remaining2.length}
        />
      )}

      {lab1 && lab2 && (
        <RedistStage
          n={3}
          label="3er laboratorio (resto)"
          candidates={candidates3}
          value={lab3}
          onChange={setLab3}
          assigned={assigned3.length}
          remaining={remaining3.length}
        />
      )}

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-200">
        <div className="text-xs font-mono-num text-slate-600">
          Asignado: <strong>{assigned1.length + assigned2.length + assigned3.length}</strong> · Sin destino: <strong>{remaining3.length}</strong>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportFile("excel")}
            disabled={!(lab1 && assigned1.length > 0) || busy === "excel"}
            data-testid="redist-export-excel"
            className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md text-sm font-bold disabled:opacity-60"
          >
            <Download className="h-3.5 w-3.5" /> Descargar Excel
          </button>
          <button
            onClick={() => exportFile("pdf")}
            disabled={!(lab1 && assigned1.length > 0) || busy === "pdf"}
            data-testid="redist-export-pdf"
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-md text-sm font-bold disabled:opacity-60"
          >
            <Download className="h-3.5 w-3.5" /> Descargar PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function RedistStage({ n, label, candidates, value, onChange, assigned, remaining }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">
        Etapa {n} · {label}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-[300px] px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
          data-testid={`redist-select-${n}`}
        >
          <option value="">— Elige laboratorio ({candidates.length} con productos disponibles) —</option>
          {candidates.map((c) => (
            <option key={c.lab} value={c.lab}>
              {c.lab} ({c.count} CN compatibles)
            </option>
          ))}
        </select>
        {value && (
          <span className="text-xs font-mono-num bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full font-semibold">
            +{assigned} CN asignados
          </span>
        )}
      </div>
      {value && (
        <div className="text-[11px] text-slate-400 mt-2 font-mono-num">
          Pendientes tras esta etapa: {remaining}
        </div>
      )}
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500">
      {msg}
    </div>
  );
}

function SectionExport({ id, section }) {
  return (
    <div className="flex gap-2 mb-3 justify-end">
      <button
        onClick={() => downloadXlsx(`/dispensations/${id}/export-section?section=${section}&format=xlsx`, `${section}.xlsx`)}
        data-testid={`export-${section}-xlsx`}
        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-200 rounded-md bg-white hover:bg-slate-50 text-slate-700"
      >
        <Download className="h-3 w-3" /> Excel
      </button>
      <button
        onClick={() => downloadXlsx(`/dispensations/${id}/export-section?section=${section}&format=pdf`, `${section}.pdf`)}
        data-testid={`export-${section}-pdf`}
        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-200 rounded-md bg-white hover:bg-slate-50 text-slate-700"
      >
        <FileText className="h-3 w-3" /> PDF
      </button>
    </div>
  );
}

/* ---------- Variability by Group (G.H.) ---------- */

function VariabilityTab({ molecules }) {
  const [openGh, setOpenGh] = useState(null);
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("generic"); // generic | total

  const filtered = useMemo(() => {
    return molecules
      .filter((m) => m.gh && m.gh !== "Sin G.H.")
      .filter((m) => !search || m.gh.toLowerCase().includes(search.toLowerCase()));
  }, [molecules, search]);

  const defaultSortKey = scope === "generic" ? "generic_lab_count" : "unique_lab_count";
  const { sorted: rows, sortKey, direction, toggle } = useSortable(filtered, defaultSortKey, "desc", "disp-variability");

  const totalLabsAll = useMemo(() => {
    const all = new Set();
    molecules.forEach((m) => {
      (m.labs_marca || []).forEach((l) => all.add(l));
      (m.labs_generico || []).forEach((l) => all.add(l));
    });
    return all.size;
  }, [molecules]);

  return (
    <div data-testid="variability-tab" className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-950">Variabilidad por grupo</h2>
            <p className="text-xs text-slate-500 mt-1 max-w-2xl">
              Grupos homogéneos ordenados por el número de laboratorios distintos del que estás dispensando. Cuanto mayor el número, más fragmentado está tu suministro: oportunidad de consolidar en menos proveedores.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs">
              <span className="font-mono-num font-bold text-slate-900">{rows.length}</span>
              <span className="text-slate-500"> G.H. · </span>
              <span className="font-mono-num font-bold text-slate-900">{totalLabsAll}</span>
              <span className="text-slate-500"> labs distintos</span>
            </div>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              data-testid="variability-scope"
              className="text-xs px-3 py-2 border border-slate-200 rounded-md bg-white"
            >
              <option value="generic">Variabilidad de genéricos</option>
              <option value="total">Variabilidad total (marca + genérico)</option>
            </select>
            <input
              data-testid="variability-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar molécula…"
              className="text-xs px-3 py-2 border border-slate-200 rounded-md bg-white min-w-[200px]"
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="variability-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left p-3 font-semibold w-8"></th>
              <SortableTh k="gh" sortKey={sortKey} direction={direction} toggle={toggle}>Molécula (G.H.)</SortableTh>
              <SortableTh k="generic_lab_count" sortKey={sortKey} direction={direction} toggle={toggle} align="right"># labs genérico</SortableTh>
              <SortableTh k="marca_lab_count" sortKey={sortKey} direction={direction} toggle={toggle} align="right"># labs marca</SortableTh>
              <SortableTh k="unique_lab_count" sortKey={sortKey} direction={direction} toggle={toggle} align="right">Total labs</SortableTh>
              <SortableTh k="generico_units" sortKey={sortKey} direction={direction} toggle={toggle} align="right">Genérico u.</SortableTh>
              <SortableTh k="marca_units" sortKey={sortKey} direction={direction} toggle={toggle} align="right">Marca u.</SortableTh>
              <SortableTh k="total_euros" sortKey={sortKey} direction={direction} toggle={toggle} align="right">Total €</SortableTh>
              <SortableTh k="state_key" sortKey={sortKey} direction={direction} toggle={toggle}>Estado</SortableTh>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className="p-12 text-center text-slate-400 text-sm">Sin grupos para los filtros aplicados.</td>
              </tr>
            )}
            {rows.map((m) => {
              const isOpen = openGh === m.gh;
              const highlight = (scope === "generic" ? m.generic_lab_count : m.unique_lab_count) || 0;
              return (
                <FragmentRow key={m.gh}>
                  <tr
                    data-testid={`variability-row-${encodeURIComponent(m.gh)}`}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => setOpenGh(isOpen ? null : m.gh)}
                  >
                    <td className="p-3 text-slate-400">
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </td>
                    <td className="p-3 font-medium text-slate-900 max-w-[360px]" title={m.gh}>{m.gh}</td>
                    <td className="p-3 text-right font-mono-num">
                      <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full font-bold ${
                        scope === "generic"
                          ? (highlight >= 4 ? "bg-rose-100 text-rose-800" : highlight >= 2 ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700")
                          : "bg-slate-100 text-slate-700"
                      }`}>
                        {m.generic_lab_count || 0}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono-num text-slate-600">{m.marca_lab_count || 0}</td>
                    <td className="p-3 text-right font-mono-num">
                      <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full font-bold ${
                        scope === "total"
                          ? (highlight >= 5 ? "bg-rose-100 text-rose-800" : highlight >= 3 ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700")
                          : "bg-slate-100 text-slate-700"
                      }`}>
                        {m.unique_lab_count || 0}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono-num text-blue-700">{fmtNum(m.generico_units)}</td>
                    <td className="p-3 text-right font-mono-num text-slate-700">{fmtNum(m.marca_units)}</td>
                    <td className="p-3 text-right font-mono-num font-semibold">{fmtEur(m.total_euros)}</td>
                    <td className="p-3"><StateBadge stateKey={m.state_key} /></td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={9} className="p-0 bg-slate-50/50">
                        <VariabilityDetail molecule={m} />
                      </td>
                    </tr>
                  )}
                </FragmentRow>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VariabilityDetail({ molecule }) {
  // Group products by laboratorio + tipo
  const groups = useMemo(() => {
    const map = {};
    (molecule.products || []).forEach((p) => {
      const key = `${p.laboratorio || "Sin lab"}__${p.type}`;
      if (!map[key]) {
        map[key] = {
          laboratorio: p.laboratorio || "Sin lab",
          type: p.type,
          units: 0,
          euros: 0,
          products: [],
        };
      }
      map[key].units += p.units;
      map[key].euros = Math.round((map[key].euros + (p.euros || 0)) * 100) / 100;
      map[key].products.push(p);
    });
    return Object.values(map).sort((a, b) => {
      // Generic first, then by units desc
      if (a.type !== b.type) return a.type === "generico" ? -1 : 1;
      return b.units - a.units;
    });
  }, [molecule]);

  return (
    <div className="p-5 space-y-4">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
        Desglose por laboratorio · {groups.length} entradas
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {groups.map((g, idx) => (
          <div
            key={`${g.laboratorio}-${g.type}-${idx}`}
            className={`bg-white border rounded-lg p-4 ${g.type === "generico" ? "border-blue-200" : "border-slate-200"}`}
          >
            <div className="flex items-start justify-between mb-2 gap-3">
              <div className="flex items-start gap-2 min-w-0 flex-1">
                <span className={`shrink-0 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                  g.type === "generico" ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-700"
                }`}>
                  {g.type === "generico" ? "GENÉRICO" : "MARCA"}
                </span>
                <span className="font-semibold text-slate-900 break-words leading-tight" title={g.laboratorio}>{g.laboratorio}</span>
              </div>
              <div className="text-right font-mono-num text-xs shrink-0 whitespace-nowrap">
                <div className="font-bold">{fmtNum(g.units)} u.</div>
                <div className="text-slate-500">{fmtEur(g.euros)}</div>
              </div>
            </div>
            <div className="space-y-1">
              {g.products.map((p) => (
                <div key={p.cn} className="flex items-center justify-between gap-2 text-xs py-1 border-t border-slate-100">
                  <div className="min-w-0 flex-1">
                    <span className="font-mono-num text-slate-500 mr-2">{p.cn}</span>
                    <span className="text-slate-700">{p.name}</span>
                  </div>
                  <div className="font-mono-num text-slate-600 whitespace-nowrap shrink-0">{fmtNum(p.units)} u.</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Bulk preferences wizard for SOLO MARCA molecules ---------- */

function BestLabsSuggestModal({ dispId, onClose, reload }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [applying, setApplying] = useState(false);
  const coveredSort = useTableSort(data?.suggestions?.filter((x) => x.suggested_lab) || []);

  useEffect(() => {
    let cancelled = false;
    api.post(`/dispensations/${dispId}/solo-marca/suggest-best-labs`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) toast.error("No se pudo calcular la sugerencia"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dispId]);

  const covered = data?.suggestions?.filter((s) => s.suggested_lab) || [];
  const uncovered = data?.suggestions?.filter((s) => !s.suggested_lab) || [];

  const apply = async () => {
    setApplying(true);
    try {
      const assignments = covered.map((s) => ({ gh: s.gh, preferred_lab: s.suggested_lab }));
      if (!assignments.length) {
        toast.error("Ningún lab con condición cubre estas moléculas");
        return;
      }
      const { data: r } = await api.post("/molecule-preferences/bulk", { assignments });
      toast.success(`${r.updated} moléculas asignadas al lab más rentable`);
      onClose();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aplicar");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="best-labs-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl font-bold flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-600" />
              Opción más rentable
            </h3>
            <p className="text-xs text-slate-600 mt-1">
              Con el descuento real de <strong>cada molécula</strong> (vademécum) o, si no la tienes pactada, el
              <strong> descuento fijo</strong> del laboratorio. Nunca se usa el descuento medio.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="best-labs-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="py-12 text-center text-sm text-slate-500">Calculando la mejor opción…</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4 text-center">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <div className="text-2xl font-bold text-emerald-800 font-mono-num">{covered.length}</div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Con condición</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="text-2xl font-bold text-amber-800 font-mono-num">{uncovered.length}</div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Sin condición</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="text-2xl font-bold text-slate-800 font-mono-num">{data?.total || 0}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold">Total Solo Marca</div>
                </div>
              </div>

              {covered.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-2">
                    Se asignarán al lab con MEJOR descuento
                  </div>
                  <div className="border border-emerald-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs" data-testid="best-labs-covered-list">
                      <thead className="bg-emerald-50 text-emerald-800">
                        <tr>
                          <SortTh field="molecule" sort={coveredSort} className="px-3 py-2">Molécula</SortTh>
                          <SortTh field="suggested_lab" sort={coveredSort} className="px-3 py-2">Lab sugerido</SortTh>
                          <SortTh field="suggested_source" sort={coveredSort} className="px-3 py-2">Origen</SortTh>
                          <SortTh field="suggested_pct" sort={coveredSort} align="right" className="px-3 py-2">% Desc.</SortTh>
                        </tr>
                      </thead>
                      <tbody>
                        {coveredSort.sorted.slice(0, 100).map((s) => (
                          <tr key={s.gh} className="border-t border-emerald-100">
                            <td className="px-3 py-1.5 truncate max-w-[280px]">{s.molecule}</td>
                            <td className="px-3 py-1.5 font-semibold">{s.suggested_lab}</td>
                            <td className="px-3 py-1.5">
                              <span
                                className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border ${
                                  s.suggested_source === "vademecum"
                                    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                                    : "bg-sky-100 text-sky-800 border-sky-200"
                                }`}
                                data-testid={`best-lab-source-${s.gh}`}
                              >
                                {s.suggested_source === "vademecum" ? "Molécula" : "Fijo"}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono-num text-emerald-700 font-bold">
                              {(s.suggested_pct || 0).toFixed(1).replace(".", ",")}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {covered.length > 100 && (
                      <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-emerald-100">
                        …y {covered.length - 100} más. Se aplicarán todas al pulsar el botón.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {uncovered.length > 0 && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <strong>{uncovered.length} moléculas</strong> no tienen ningún lab EFG con condición cargada que las cubra. Se dejarán sin asignar. Puedes usar después &quot;Asignar 3 proveedores&quot; para completarlas manualmente.
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-600">
            {covered.length > 0 ? `Se guardarán ${covered.length} preferencias.` : "Nada que asignar."}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-xs px-3 py-2 border border-slate-300 rounded-md hover:bg-slate-50">
              Cancelar
            </button>
            <button onClick={apply} disabled={!covered.length || applying}
              data-testid="apply-best-labs"
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-xs font-semibold">
              {applying ? "Guardando…" : (<><Check className="h-3 w-3" /> Aplicar sugerencia</>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BulkPrefsWizard({ molecules, reload, onDone }) {
  const [lab1, setLab1] = useState("");
  const [lab2, setLab2] = useState("");
  const [lab3, setLab3] = useState("");

  const ghs = useMemo(() => molecules.map((m) => m.gh), [molecules]);

  // For each lab, list of GHs from this set that have the lab in available_labs
  const labGhCount = useMemo(() => {
    const map = {};
    molecules.forEach((m) => {
      (m.available_labs || []).forEach((lab) => {
        if (!map[lab]) map[lab] = new Set();
        map[lab].add(m.gh);
      });
    });
    return map;
  }, [molecules]);

  const ghsForLab = (ghPool, lab) => {
    if (!lab) return [];
    const set = labGhCount[lab];
    if (!set) return [];
    return ghPool.filter((g) => set.has(g));
  };

  const cands1 = useMemo(() =>
    Object.keys(labGhCount).map((lab) => ({ lab, count: ghsForLab(ghs, lab).length }))
      .filter((x) => x.count > 0).sort((a, b) => b.count - a.count),
  [labGhCount, ghs]);
  const assigned1 = lab1 ? ghsForLab(ghs, lab1) : [];
  const rem1 = ghs.filter((g) => !assigned1.includes(g));
  const cands2 = useMemo(() =>
    !lab1 ? [] : Object.keys(labGhCount).filter((l) => l !== lab1)
      .map((lab) => ({ lab, count: ghsForLab(rem1, lab).length }))
      .filter((x) => x.count > 0).sort((a, b) => b.count - a.count),
  [labGhCount, rem1, lab1]);
  const assigned2 = lab2 ? ghsForLab(rem1, lab2) : [];
  const rem2 = rem1.filter((g) => !assigned2.includes(g));
  const cands3 = useMemo(() =>
    !lab1 || !lab2 ? [] : Object.keys(labGhCount).filter((l) => l !== lab1 && l !== lab2)
      .map((lab) => ({ lab, count: ghsForLab(rem2, lab).length }))
      .filter((x) => x.count > 0).sort((a, b) => b.count - a.count),
  [labGhCount, rem2, lab1, lab2]);
  const assigned3 = lab3 ? ghsForLab(rem2, lab3) : [];
  const rem3 = rem2.filter((g) => !assigned3.includes(g));

  const apply = async () => {
    const buildAssignments = (lab, ghList) =>
      lab && ghList.length ? ghList.map((gh) => ({ gh, preferred_lab: lab })) : [];
    const assignments = [
      ...buildAssignments(lab1, assigned1),
      ...buildAssignments(lab2, assigned2),
      ...buildAssignments(lab3, assigned3),
    ];
    if (!assignments.length) return toast.error("Elige al menos un laboratorio");
    try {
      const { data } = await api.post("/molecule-preferences/bulk", { assignments });
      toast.success(`Asignadas ${data.updated} moléculas a sus proveedores preferidos`);
      onDone();
      reload();
    } catch (e) {
      toast.error("Error al guardar las preferencias");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4 space-y-3" data-testid="bulk-prefs-wizard">
      <div className="text-xs text-slate-700 leading-relaxed bg-amber-50 border border-amber-200 rounded-md p-3">
        <strong>Asignación masiva de proveedores</strong> para las {molecules.length} moléculas SOLO MARCA. El primer
        laboratorio absorbe las que tenga en su vademécum, el segundo las restantes, el tercero el resto.
        Esto guarda la <strong>compra preferida</strong> de cada molécula.
      </div>
      <RedistStage n={1} label="1er laboratorio" candidates={cands1} value={lab1}
        onChange={(v) => { setLab1(v); setLab2(""); setLab3(""); }}
        assigned={assigned1.length} remaining={rem1.length} />
      {lab1 && (
        <RedistStage n={2} label="2º laboratorio (restantes)" candidates={cands2} value={lab2}
          onChange={(v) => { setLab2(v); setLab3(""); }}
          assigned={assigned2.length} remaining={rem2.length} />
      )}
      {lab1 && lab2 && (
        <RedistStage n={3} label="3er laboratorio (resto)" candidates={cands3} value={lab3}
          onChange={setLab3} assigned={assigned3.length} remaining={rem3.length} />
      )}
      <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-200">
        <div className="text-xs font-mono-num text-slate-600">
          Asignadas: <strong>{assigned1.length + assigned2.length + assigned3.length}</strong> · Sin destino: <strong>{rem3.length}</strong>
        </div>
        <button onClick={apply} disabled={!(lab1 && assigned1.length > 0)}
          data-testid="apply-bulk-prefs"
          className="bg-slate-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 disabled:opacity-60">
          Guardar preferencias
        </button>
      </div>
    </div>
  );
}

/* ---------- Modal: % de empleados por molécula ---------- */
function UnassignedCoverageModal({ dispId, reload, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const rankSort = useTableSort(data?.ranking || []);
  const unassignedSort = useTableSort(data?.unassigned || []);

  useEffect(() => {
    api
      .get(`/dispensations/${dispId}/solo-marca/unassigned-coverage`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "No se pudo calcular la cobertura"));
  }, [dispId]);

  const assignLab = async (lab, ghs) => {
    setAssigning(lab);
    try {
      await api.post("/molecule-preferences/bulk", {
        assignments: ghs.map((gh) => ({ gh, preferred_lab: lab })),
      });
      toast.success(`${ghs.length} moléculas asignadas a ${lab}`);
      onClose();
      reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron asignar");
    } finally {
      setAssigning(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="unassigned-coverage-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Solo Marca · sin proveedor</div>
            <div className="mt-1 font-display text-2xl font-bold tracking-tight">Moléculas sin proveedor asignado</div>
            {data && (
              <div className="text-xs text-slate-600 mt-1 font-mono-num">
                {data.total_unassigned} moléculas · {fmtNum(data.total_marca_units)} uds marca · {fmtEur(data.total_marca_euros)}
                {data.without_any_lab?.length ? ` · ${data.without_any_lab.length} sin ningún genérico disponible` : ""}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100" data-testid="unassigned-coverage-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          {error && <div className="text-sm text-rose-600">{error}</div>}
          {!error && !data && <div className="text-sm text-slate-500">Calculando cobertura…</div>}
          {data && data.total_unassigned === 0 && (
            <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              Todas las moléculas Solo Marca y Marginal ya tienen proveedor asignado. 🏆
            </div>
          )}
          {data && data.total_unassigned > 0 && (
            <>
              {data.best_lab && (
                <div className="bg-sky-50 border border-sky-200 rounded-xl p-4" data-testid="coverage-best-lab">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-sky-800">Proveedor que más cubre</div>
                  <div className="flex flex-wrap items-center justify-between gap-3 mt-1">
                    <div>
                      <div className="font-display text-xl font-bold">{data.best_lab.laboratorio}</div>
                      <div className="text-xs text-slate-600 font-mono-num mt-0.5">
                        cubre {data.best_lab.covers} de {data.total_unassigned} ({data.best_lab.covers_pct}%) · {fmtEur(data.best_lab.marca_euros)} en marca
                        {data.best_lab.has_agreement
                          ? ` · ${data.best_lab.discount_effective_pct || 0}% real (${data.best_lab.ghs_with_condition}/${data.best_lab.covers} con condición)`
                          : " · sin condición cargada"}
                      </div>
                    </div>
                    <button
                      onClick={() => assignLab(data.best_lab.laboratorio, data.best_lab.ghs)}
                      disabled={assigning === data.best_lab.laboratorio}
                      className="inline-flex items-center gap-1.5 bg-sky-700 hover:bg-sky-800 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                      data-testid="coverage-assign-best"
                    >
                      <Check className="h-4 w-4" /> Asignar a las {data.best_lab.covers} que cubre
                    </button>
                  </div>
                </div>
              )}

              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Ranking de cobertura</div>
                <table className="w-full text-sm" data-testid="coverage-ranking">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b-2 border-slate-200">
                      <SortTh field="laboratorio" sort={rankSort} className="py-2">Laboratorio</SortTh>
                      <SortTh field="covers" sort={rankSort} align="right" className="py-2">Cubre</SortTh>
                      <SortTh field="covers_pct" sort={rankSort} align="right" className="py-2">%</SortTh>
                      <SortTh field="marca_euros" sort={rankSort} align="right" className="py-2">€ marca</SortTh>
                      <SortTh field="discount_effective_pct" sort={rankSort} align="right" className="py-2">Condición real</SortTh>
                      <th className="text-right py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankSort.sorted.map((r) => (
                      <tr key={r.laboratorio} className="border-b border-slate-100" data-testid={`coverage-row-${r.laboratorio}`}>
                        <td className="py-2 max-w-[240px]"><span className="block truncate" title={r.laboratorio}>{r.laboratorio}</span></td>
                        <td className="py-2 text-right font-mono-num font-semibold">{r.covers}</td>
                        <td className="py-2 text-right font-mono-num text-slate-500">{r.covers_pct}%</td>
                        <td className="py-2 text-right font-mono-num">{fmtEur(r.marca_euros)}</td>
                        <td className="py-2 text-right text-xs">
                          {r.has_agreement ? (
                            <span className="text-emerald-700 font-semibold" title={`${r.ghs_via_vademecum} por vademécum · ${r.ghs_via_linear} con descuento fijo`}>
                              {r.discount_effective_pct ? `${r.discount_effective_pct}%` : "sí"}
                              <span className="block text-[10px] font-normal text-slate-500 font-mono-num">
                                {r.ghs_with_condition}/{r.covers} con condición
                              </span>
                            </span>
                          ) : <span className="text-slate-400">no</span>}
                        </td>
                        <td className="py-2 text-right">
                          <button
                            onClick={() => assignLab(r.laboratorio, r.ghs)}
                            disabled={assigning === r.laboratorio}
                            className="text-xs px-2.5 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-60"
                          >
                            Asignar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Moléculas sin proveedor</div>
                  <button
                    onClick={() => downloadXlsx(`/dispensations/${dispId}/solo-marca-list?format=xlsx`, "solo_marca_listado.xlsx")}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50"
                    data-testid="coverage-export-list"
                  >
                    <Download className="h-3 w-3" /> Excel del listado
                  </button>
                </div>
                <table className="w-full text-sm" data-testid="coverage-unassigned-list">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b-2 border-slate-200">
                      <SortTh field="gh" sort={unassignedSort} className="py-2">Molécula (G.H.)</SortTh>
                      <SortTh field="state_label" sort={unassignedSort} className="py-2">Estado</SortTh>
                      <SortTh field="marca_units" sort={unassignedSort} align="right" className="py-2">Uds marca</SortTh>
                      <SortTh field="marca_units_per_month" sort={unassignedSort} align="right" className="py-2">u/mes</SortTh>
                      <SortTh field="marca_euros" sort={unassignedSort} align="right" className="py-2">€ marca</SortTh>
                      <SortTh field="n_labs" sort={unassignedSort} align="right" className="py-2">Labs EFG</SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {unassignedSort.sorted.map((u) => (
                      <tr key={u.gh} className="border-b border-slate-100">
                        <td className="py-2 max-w-[300px]"><span className="block truncate" title={u.gh}>{u.gh}</span></td>
                        <td className="py-2 text-xs text-slate-500">{u.state_label}</td>
                        <td className="py-2 text-right font-mono-num">{fmtNum(u.marca_units)}</td>
                        <td className="py-2 text-right font-mono-num text-slate-500">{u.marca_units_per_month}</td>
                        <td className="py-2 text-right font-mono-num font-semibold">{fmtEur(u.marca_euros)}</td>
                        <td className={`py-2 text-right font-mono-num ${u.n_labs === 0 ? "text-rose-600 font-bold" : "text-slate-500"}`}>{u.n_labs}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MoleculeEmployeeEvolution({ empId, gh }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api
      .get(`/employees/${empId}/molecule-evolution`, { params: { gh } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "No se pudo cargar la evolución"); });
    return () => { cancelled = true; };
  }, [empId, gh]);

  if (error) return <div className="text-xs text-rose-600 px-2 py-3" data-testid={`molecule-employee-evolution-${empId}`}>{error}</div>;
  if (!data) return <div className="text-xs text-slate-500 px-2 py-3" data-testid={`molecule-employee-evolution-${empId}`}>Cargando evolución…</div>;
  if (!data.points?.length) {
    return (
      <div className="text-xs text-slate-500 px-2 py-3" data-testid={`molecule-employee-evolution-${empId}`}>
        Sin histórico de esta molécula para {data.employee?.name}: sólo aparece en este estudio.
      </div>
    );
  }
  const chart = data.points.map((p) => ({
    label: p.label || p.period_end || "",
    pct: p.pct,
    team: p.team_pct,
    marca: p.marca_units,
    gen: p.gen_units,
  }));
  const up = data.delta_pp >= 0;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-1" data-testid={`molecule-employee-evolution-${empId}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
          Evolución de {data.employee?.name} en esta molécula
        </div>
        <div className={`text-xs font-mono-num font-bold ${up ? "text-emerald-600" : "text-rose-600"}`}>
          {up ? "+" : ""}{data.delta_pp.toFixed(1)} pp · mejor {data.best_pct.toFixed(1)}%
        </div>
      </div>
      <div style={{ width: "100%", height: 190 }}>
        <ResponsiveContainer>
          <LineChart data={chart} margin={{ top: 5, right: 12, bottom: 0, left: -18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} unit="%" domain={[0, 100]} />
            <Tooltip formatter={(v, k) => [`${v}%`, k === "pct" ? "Empleado" : "Equipo"]} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="pct" name="Empleado" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="team" name="Equipo" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-slate-500 uppercase tracking-wider">
              <th className="text-left py-1">Estudio</th>
              <th className="text-right py-1">Marca</th>
              <th className="text-right py-1">Genérico</th>
              <th className="text-right py-1">% Cambio</th>
              <th className="text-right py-1">Equipo</th>
            </tr>
          </thead>
          <tbody>
            {data.points.map((p) => (
              <tr key={p.disp_id} className="border-t border-slate-200">
                <td className="py-1 text-slate-700">{p.label}</td>
                <td className="py-1 text-right font-mono-num text-rose-700">{p.marca_units}</td>
                <td className="py-1 text-right font-mono-num text-emerald-700">{p.gen_units}</td>
                <td className="py-1 text-right font-mono-num font-bold">{p.pct.toFixed(1)}%</td>
                <td className="py-1 text-right font-mono-num text-slate-500">{p.team_pct.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MoleculeEmployeesModal({ dispensationId, gh, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openEmp, setOpenEmp] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    api
      .get(`/dispensations/${dispensationId}/molecule-employees`, { params: { gh } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "No se pudo cargar el reparto"); });
    return () => { cancelled = true; };
  }, [dispensationId, gh]);
  const stop = (e) => e.stopPropagation();
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="molecule-employees-modal">
      <div className="bg-white text-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={stop}>
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold flex items-center gap-2">
              <Users className="h-3.5 w-3.5" /> Reparto por empleado · G.H.
            </div>
            <div className="mt-1 font-display text-lg font-semibold truncate" title={gh}>{gh}</div>
            {data?.has_breakdown && (
              <div className="text-xs text-slate-500 mt-1 font-mono-num flex flex-wrap gap-x-3 gap-y-0.5">
                <span>Oportunidades: <strong>{data.total_units}</strong> u</span>
                <span>Marca: <strong>{data.total_marca}</strong></span>
                <span>Genérico: <strong>{data.total_generic}</strong></span>
                <span>Media cambio: <strong>{data.pct_change_avg?.toFixed?.(1) ?? data.pct_change_avg}%</strong></span>
                <span>{data.by_employee.length} empleado(s)</span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100"
            data-testid="molecule-employees-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          {error && <div className="text-sm text-rose-600" data-testid="molecule-employees-error">{error}</div>}
          {!error && !data && <div className="text-sm text-slate-500">Cargando…</div>}
          {data && data.has_breakdown === false && (
            <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-4">
              Este estudio no tiene desglose por empleado (formato de origen sin matriz).
            </div>
          )}
          {data && data.has_breakdown && data.by_employee.length > 0 && (
            <>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold mb-3 grid grid-cols-12 gap-2 px-1">
                <div className="col-span-5">Empleado</div>
                <div className="col-span-2 text-right">Oportunidades</div>
                <div className="col-span-2 text-right">Marca</div>
                <div className="col-span-1 text-right">EFG</div>
                <div className="col-span-2 text-right">% Cambio</div>
              </div>
              <ul className="space-y-2" data-testid="molecule-employees-list">
                {data.by_employee.map((e) => {
                  const pct = e.pct_change ?? e.pct ?? 0;
                  const barColor = pct >= 70 ? "bg-emerald-600" : pct >= 40 ? "bg-amber-500" : "bg-rose-500";
                  const open = openEmp === e.employee_id;
                  return (
                    <li key={e.employee_id}>
                      <button
                        type="button"
                        onClick={() => setOpenEmp(open ? null : e.employee_id)}
                        className={`w-full text-left grid grid-cols-12 gap-2 items-center py-2 px-1 rounded transition-colors ${open ? "bg-slate-100" : "hover:bg-slate-50"}`}
                        data-testid={`molecule-employee-${e.employee_id}`}
                        title="Ver su evolución en esta molécula"
                      >
                        <div className="col-span-5 min-w-0">
                          <div className="text-sm font-medium truncate flex items-center gap-1.5">
                            {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
                            {e.name}{e.role ? <span className="text-slate-500 font-normal ml-1">· {e.role}</span> : null}
                          </div>
                          <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
                          </div>
                        </div>
                        <div className="col-span-2 text-right text-sm font-mono-num font-semibold">{e.opportunities ?? e.units}</div>
                        <div className="col-span-2 text-right text-sm font-mono-num text-rose-700">{e.units_marca ?? "—"}</div>
                        <div className="col-span-1 text-right text-sm font-mono-num text-emerald-700">{e.units_generic ?? "—"}</div>
                        <div className="col-span-2 text-right text-sm font-mono-num font-bold">{pct.toFixed(1)}%</div>
                      </button>
                      {open && <MoleculeEmployeeEvolution empId={e.employee_id} gh={gh} />}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-4 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3 leading-relaxed">
                <strong>Cómo leerlo:</strong> {'"'}Oportunidades{'"'} son todas las unidades de esta molécula que ha dispensado el empleado (marca + genérico).
                {' "'}% Cambio{'"'} = genérico ÷ oportunidades. Cuanto más alto, mejor.
                <br />Haz <strong>click en un empleado</strong> para ver su evolución en esta molécula estudio a estudio.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

